/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import type { GraphErrorFlags, GraphMeeting } from "@/api/home-context"
import type {
  ChatAppResponse,
  ChatSessionModel,
  GPTModelItem,
  PromptLibraryItem,
  WorkflowItem,
} from "@/api/models"
import type { LocalFilePreviewPanelOptions } from "@/features/chat/components/local-file-preview-sidebar"
import type { FlowRun } from "@/features/workflow-editor/hooks/use-workflow-runs"
import type { M365Type } from "@/utils/citation-utils"

import { createDocumentApi, warmExplorerCachesApi } from "@/api/api"
import { refreshGraphApi } from "@/api/home-context"
import { Eye, PanelRightClose, PanelRightOpen, Play, Share2 } from "@/components/icons"
import { HeaderRightSlot } from "@/components/layouts/app-layout/header-right-slot"
import { TextSelectionProvider } from "@/components/text-selection-menu/text-selection-provider"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { SidebarInset } from "@/components/ui/sidebar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useRoutes } from "@/contexts/use-routes"
import { AgentFailureRecovery } from "@/features/agents/components/agent-failure-recovery"
import {
  sessionErrorAlreadyInThread,
  threadFailureRecoveryActions,
} from "@/features/agents/utils/agent-presence-utils"
import { AgentModeInfoModal } from "@/features/ai-explorer/components/agent-mode-info-modal"
import { AgentRunningBanner } from "@/features/ai-explorer/components/AgentRunningBanner"
import {
  DeepExplorerPanelActions,
  DeepExplorerPanelContent,
  DeepExplorerPanelTitle,
} from "@/features/ai-explorer/components/deep-explorer-side-panel/DeepExplorerSidePanel"
import { showDeepModeOnToast } from "@/features/ai-explorer/components/deep-mode-toast"
import { ExplorerFooterToolbar } from "@/features/ai-explorer/components/explorer-footer-toolbar"
import { ImageReasoningWarningModal } from "@/features/ai-explorer/components/image-reasoning-warning-modal"
import { M365InfoModal } from "@/features/ai-explorer/components/m365-info-modal"
import { buildAgentRunChatPath } from "@/features/ai-explorer/components/research-runs-panel/research-runs-routing"
import { ResearchRunsHeaderButton } from "@/features/ai-explorer/components/research-runs-panel/ResearchRunsHeaderButton"
import { ResearchRunsPanel } from "@/features/ai-explorer/components/research-runs-panel/ResearchRunsPanel"
import { ResearchSettingsSidebar } from "@/features/ai-explorer/components/research-settings-sidebar"
import { SmartFollowupCards } from "@/features/ai-explorer/components/SmartFollowupCards"
import {
  composeFollowupQuestion,
  type FollowupSuggestion,
  parseFollowupMessage,
} from "@/features/ai-explorer/components/SmartFollowupCards-utils"
import { VoiceModeInfoModal } from "@/features/ai-explorer/components/voice-mode-info-modal"
import { isActiveAskUserPrompt } from "@/features/ai-explorer/components/workspace/ask-user-correlation"
import { DeepExplorerActivityPeek } from "@/features/ai-explorer/components/workspace/DeepExplorerActivityPeek"
import { dispatchPlanCancel } from "@/features/ai-explorer/components/workspace/plan-cancel-dispatch"
import {
  WORKSPACE_LAYOUT_ENABLED,
  WorkspaceLayout,
} from "@/features/ai-explorer/components/workspace/WorkspaceLayout"
import {
  isActiveAgentRunStatus,
  useAgentRunsQuery,
} from "@/features/ai-explorer/hooks/use-agent-runs-query"
import { useAIExplorerChat } from "@/features/ai-explorer/hooks/use-ai-explorer-chat"
import { useCitationVerificationBadges } from "@/features/ai-explorer/hooks/use-citation-verification-badges"
import { useDeepLivePoll } from "@/features/ai-explorer/hooks/use-deep-live-poll"
import { usePersonalSearch } from "@/features/ai-explorer/hooks/use-personal-search"
import { useProjectDefaults } from "@/features/ai-explorer/hooks/use-project-defaults"
import { useUncancellableFlowRunSessions } from "@/features/ai-explorer/hooks/use-uncancellable-flow-run-sessions"
import { getAnswersForReferenceItems } from "@/features/ai-explorer/utils/answer-reference-selection"
import { removeExplorerContextFile } from "@/features/ai-explorer/utils/context-file-removal"
import {
  getDeepLayoutMode,
  getSharedSidebarTarget,
} from "@/features/ai-explorer/utils/deep-layout-mode"
import {
  getResearchRunsPanelAction,
  RESEARCH_RUNS_PANEL_ID,
  RESEARCH_RUNS_PANEL_WIDTH_PX,
} from "@/features/ai-explorer/utils/research-runs-panel"
import { useTranslatorTool } from "@/features/ai-translator/hooks/use-translator-tool"
import { BaseSettingsSidebar } from "@/features/base-settings/components/base-settings-sidebar"
import { useBaseSettings } from "@/features/base-settings/hooks/use-base-settings"
import { openCanvasInSidebar } from "@/features/canvas/utils/canvas-sidebar"
import { Chat } from "@/features/chat/components/chat"
import { ChatHome } from "@/features/chat/components/chat-home"
import { ChatSelectedFilesContextPanel } from "@/features/chat/components/chat-selected-files-context-panel"
import {
  buildChatSelectedFileItems,
  getChatSelectedFileItemLabels,
} from "@/features/chat/components/chat-selected-files-utils"
import { ChatShareDialog } from "@/features/chat/components/chat-share-dialog"
import { ComposerQueueBar } from "@/features/chat/components/composer-queue-bar"
import { ExplorerLoadingState } from "@/features/chat/components/explorer-loading-state"
import { openLocalFilePreviewPanel } from "@/features/chat/components/local-file-preview-sidebar"
import { MemoryOffTransferredNotice } from "@/features/chat/components/memory-off-transferred-notice"
import { SharedHistoryMarkerLine } from "@/features/chat/components/shared-history-marker"
import { StartNewChat } from "@/features/chat/components/start-new-chat"
import { getAnswerReferenceItems } from "@/features/chat/components/supporting-content-utils"
import { SystemMessageLayout } from "@/features/chat/components/system-message-layout"
import { ToolApprovalOverlay } from "@/features/chat/components/tool-approval-overlay"
import {
  getChatRequestGenerationId,
  subscribeChatRequestRegistry,
} from "@/features/chat/hooks/chat-request-registry"
import { useChatHistory } from "@/features/chat/hooks/use-chat-history"
import { useHomeContext } from "@/features/chat/hooks/use-home-context"
import { useRotatingPlaceholder } from "@/features/chat/hooks/use-rotating-placeholder"
import { useSessionsQuery } from "@/features/chat/hooks/use-sessions-query"
import { useSharedHistoryMarkers } from "@/features/chat/hooks/use-shared-history-markers"
import { useSteerActions } from "@/features/chat/hooks/use-steer-actions"
import { useVoiceEngine } from "@/features/chat/hooks/use-voice-engine"
import { getRetryRequestForLastQuestion } from "@/features/chat/utils/chat-request-utils"
import { getSessionErrorCode } from "@/features/chat/utils/session-error-code"
import { hasVoiceInternalDataConsent } from "@/features/chat/utils/voice-mode-consent"
import { SpreadsheetViewer } from "@/features/file-viewer/components/spreadsheet-viewer"
import { useReadSpreadsheetData } from "@/features/file-viewer/hooks/use-read-spreadsheet-data"
import { useOnboardingOverlayActive } from "@/features/onboarding/onboarding-overlay-context"
import { ProjectsDialog } from "@/features/projects/components/projects-dialog"
import { useCurrentSessionProject } from "@/features/projects/hooks/use-current-session-project"
import { WorkflowInputDialog } from "@/features/workflow-editor/components/workflow-input-dialog"
import { getPendingWorkflowConsumption } from "@/features/workflow-editor/utils/pending-workflow-run-guard"
import {
  createWorkflowRunPendingChatResponse,
  createWorkflowRunRequestErrorChatResponse,
  getWorkflowRunChatQuestion,
  getWorkflowRunFromApiError,
  persistWorkflowRunStatusChatResponse,
} from "@/features/workflow-editor/utils/workflow-run-chat-utils"
import { workflowRequiresInput } from "@/features/workflow-editor/utils/workflow-run-utils"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useContextMenuActions } from "@/hooks/use-context-menu-actions"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useMemoryOffTransferred } from "@/hooks/use-memory-off-transferred"
import { useProject, useProjectFiles, useProjects } from "@/hooks/use-projects"
import {
  useAgentModeEnabled,
  useDeepExplorerEnabled,
  useM365Enabled,
  useNetboxEnabled,
  usePersonalizedHomeEnabled,
  usePluginsEnabled,
  usePromptsEnabled,
  useServiceDeskEnabled,
  useVoiceModeEnabled,
  useWebSearchEnabled,
  useXOneEnabled,
} from "@/hooks/use-route-overrides"
import { useMarkSessionNotificationViewed } from "@/hooks/use-session-sharing"
import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { cn } from "@/lib/utils"
import { ChatService } from "@/services/chat-service"
import { SettingsService } from "@/services/settings-service"
import { getDeepStateForSession, useAIExplorerStore } from "@/stores/ai-explorer-store"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import { useNavigatorStore } from "@/stores/navigator-store"
import { usePendingMessageStore } from "@/stores/pending-message-store"
import { usePendingWorkflowStore } from "@/stores/pending-workflow-store"
import { usePreferencesStore } from "@/stores/preferences-store"
import { useResearchPreferencesStore } from "@/stores/research-preferences-store"
import { defaultSessionState } from "@/stores/slices/chat-slice"
import { useWorkflowRunStore } from "@/stores/workflow-run-store"
import { getDocuments, getResponseContent, getResponseContext } from "@/utils/response-format-utils"

import {
  acceptedFileTypes,
  acceptedFileTypesWithoutImages,
  azureOpenAIAcceptedFileTypes,
  azureOpenAIAcceptedFileTypesWithoutImages,
  MAX_FILES_UPLOAD,
} from "./ai-explorer-constants"
import {
  getArtifactSessionProposedTodos,
  shouldAutoPromptM365Consent,
} from "./ai-explorer-page-utils"

// Every key `GraphErrorFlags` can carry (see api/home-context.ts) — used to detect
// a total Graph outage (all legs failed) as opposed to one flaky signal.
const GRAPH_ERROR_LEG_KEYS: Array<keyof GraphErrorFlags> = [
  "calendar",
  "recent_files",
  "unread_mail",
  "trending",
  "people",
]

export function AIExplorerPage() {
  const { projectId: urlProjectId } = useParams<{
    projectId?: string
    sessionId?: string
  }>()
  const navigate = useNavigate()
  const { getRouteChatPath, getRouteById, routes } = useRoutes()

  // Determine session type from current route
  const sessionType = useCurrentSessionType()
  const voiceSessionRoutePrefix = getRouteChatPath(sessionType)

  // Get projectId from URL params first, fallback to session's projectId
  const sessionProjectId = useCurrentSessionProject()
  const projectId = urlProjectId ?? sessionProjectId ?? undefined

  // Fetch projects and project files using React Query hooks
  useProjects(sessionType)
  useProjectFiles(projectId)
  const { data: activeProject } = useProject(projectId, sessionType)

  // Fetch sessions to check if current user is a reader
  const { data: sessions = [] } = useSessionsQuery(sessionType, projectId)
  // Get config for file type restrictions
  const { data: config } = useConfigQuery()
  const useResponsesFileSearch = config?.useResponsesFileSearch ?? false

  // Get DB-backed settings for model and follow-ups
  const {
    gptModel: dbGptModel,
    availableGptModels,
    suggestFollowupQuestions,
    handleGptModelChange,
    modelSettingsHydrated,
  } = useBaseSettings({ sessionType })

  // Used by `edit_in_canvas` document-action handler to invalidate the
  // documents list after `createDocumentApi` so the new doc appears in
  // the sidebar's Documents folder right away.
  const queryClient = useQueryClient()

  // Get namespaced stores for this sessionType
  const explorerStore = useAIExplorerStore(sessionType)

  // The page is the only owner of URL sessionId → ChatService sync (#3515): the
  // sidebar's ChatHistory is list-only, and below the 1024px mobile breakpoint
  // it is an unmounted Sheet. Same unscoped sessions query as the sidebar list,
  // so both share a cache.
  useChatHistory({ sessionType, useChatStore: () => explorerStore })

  const deepFollowups = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).followupSuggestions
  )
  const navigatorStore = useNavigatorStore(sessionType)
  const researchPrefsStore = useResearchPreferencesStore(sessionType)

  // Deep mode: count in-progress todos for the active-count badge
  const activeTodoCount = explorerStore(
    (state) =>
      getDeepStateForSession(state, state.currentSessionId).todos.filter(
        (todo) => todo.status === "in_progress"
      ).length
  )
  const deepPlanProposed = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).planProposed
  )
  const deepPlanApproved = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).planApproved
  )
  // True while a plan card is showing and the user hasn't approved yet —
  // the streaming run is paused on the backend interrupt() gate. The chat
  // should treat this differently from an active "thinking" state: no stop
  // button, and a placeholder that says we're waiting on the user.
  const isDeepAwaitingPlanApproval = deepPlanProposed && !deepPlanApproved
  const deepProposedTodos = explorerStore(
    (state) => getDeepStateForSession(state, state.currentSessionId).proposedTodos
  )
  const setDeepPlanProposed = explorerStore((state) => state.setDeepPlanProposed)
  const replaceDeepState = explorerStore((state) => state.replaceDeepState)
  const deepPanelOpen = explorerStore((state) => state.panelOpen)
  const setDeepPanelOpen = explorerStore((state) => state.setDeepPanelOpen)

  // Preload defaults from project into stores on mount/change
  const setSourceUrls = researchPrefsStore((state) => state.setSourceUrls)
  const setTrustedMode = researchPrefsStore((state) => state.setTrustedMode)
  const sourceUrlsInput = researchPrefsStore((state) => state.prefs.sourceUrls)
  const trustedMode = researchPrefsStore((state) => state.prefs.trustedMode)
  const [showProjectsPanel, setShowProjectsPanel] = useState(false)
  const { t } = useTranslation()
  // AI Explorer store selectors for session management
  const chatState = explorerStore((state) => {
    const currentId = state.currentSessionId
    return currentId ? state.sessionStates[currentId] || defaultSessionState : defaultSessionState
  })
  // Decorate citation pills with green/yellow fact-checker badges. During active
  // streaming, avoid a document-wide MutationObserver on every markdown chunk;
  // the hook re-stamps once streaming settles.
  useCitationVerificationBadges(sessionType, { disabled: chatState.isStreaming })
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setCurrentSessionId = explorerStore((state) => state.setCurrentSessionId)
  const setSelectedPrompt = explorerStore((state) => state.setSelectedPrompt)
  const responseTiming = explorerStore((state) => state.responseTiming)

  // Explorer steering (issue #2438). `injectEnabled` is false whenever the
  // explorer_steer flag is off (or the user lacks access), and `steerReady`
  // only ever flips true for a running turn on the Explorer main-chat WS —
  // so with the flag off both booleans below are constant false and the
  // composer props further down are byte-identical to today.
  const {
    injectEnabled: steerInjectEnabled,
    stopRedirectEnabled: steerStopRedirectEnabled,
    sendSteerNote,
  } = useSteerActions({
    useChatStore: () => explorerStore,
    // Read lazily at send time, never captured: the token rotates, and a
    // durable note posted late in a long turn must use the current one.
    getDurableToken: () => SettingsService.getInstance().token,
  })

  // Navigator store for libraries
  const toggleLibraryChecked = navigatorStore((state) => state.toggleLibraryChecked)
  const enabledLibraries = navigatorStore((state) => state.enabledLibraries)
  const citationLibraryDocuments = navigatorStore((state) => state.citationLibraryDocuments)

  const {
    exampleQuestions,
    handleExampleClicked,
    files,
    selectedDocuments,
    filesInIndex,
    sessionFileRefs,
    handleAddFiles,
    handleResetFiles,
    handleRemoveFile,
    handleSendQuestion,
    handleCancelRequest,
    chatMessageStreamEnd,
    aborted,
    currentSessionId,
    defaultSearchServiceId,
    moduleName,
    handleStartNewChat,
    handleSharedLeaveRedirect,
    handleWebSearchChange,
    enableWebSearch,
    enableTreeRetrieval,
    handleTreeRetrievalChange,
    enableM365,
    handleM365Change,
    enableServiceDesk,
    handleServiceDeskChange,
    enableNetbox,
    handleNetboxChange,
    enableXOne,
    handleXOneChange,
    enablePlugins,
    handlePluginsChange,
    disabledPluginIds,
    handlePluginDisabledChange,
    handleInstalledPluginsChange,
    enableAgent,
    handleAgentChange,
    enableDeep,
    handleDeepChange,
    openSessionInDeepMode,
    sendPlanDecision,
    sendArtifactDecision,
    sendAskUserResponse,
    rewindToPlan,
    sendUserNote,
    totalTokenCount,
    changeTokenCounts,
    showAgentModeInfo,
    setShowAgentModeInfo,
    showM365Info,
    setShowM365Info,
    hasM365Approval,
    hasDeclinedM365,
    isM365ApprovalLoading,
    handleM365Approve,
    handleM365Decline,
    handleM365Dismiss,
    handleContinue,
    reasoningMode,
    handleReasoningModeChange,
    modelOverride,
    setModelOverride,
    effortOverride,
    setEffortOverride,
    effectiveModel: selectedReasoningModel,
    selectableModels,
    handleHighReasoningChange,
    showImageReasoningWarning,
    handleImageReasoningWarningClose,
    handleImageReasoningWarningCancel,
    isReasoningModelActive,
    hasLatestPlan,
    handleStartResearch,
    handleGraphFileSelected,
    handleDocumentsSelected,
    handleRemoveDocument,
    handleRemoveIndexedFile,
    removingFileName,
    markNextMessageAsPromptTemplate,
    reattachToActiveRun,
    isMessageQueueEnabled,
    handleEnqueueMessage,
    handleRemoveQueuedMessage,
    requestForceRedirect,
  } = useAIExplorerChat()
  const { clearUncancellableFlowRun, isCurrentSessionUncancellable, markUncancellableFlowRun } =
    useUncancellableFlowRunSessions(explorerStore, currentSessionId)

  // On mount: warm backend caches. The session itself is created on send
  // (#1727 first moved creation from mount to the first keystroke; #2963
  // moved it to send). Creating sessions before a message is sent left empty
  // sessions littering chat history for every abandoned draft.
  useEffect(() => {
    // Pass a getter (not a snapshot) so the request always reads the live
    // token, and so the single retry inside warmExplorerCachesApi can pick
    // up a token that lands in SettingsService just after this effect fires
    // instead of repeating the same (possibly still-empty) value.
    void warmExplorerCachesApi(() => SettingsService.getInstance().token)
  }, [])

  // Check if current user is a reader (cannot send messages)
  const currentSession = useMemo(
    () => sessions.find((s) => s != null && s.id === currentSessionId),
    [sessions, currentSessionId]
  )
  const memoryOffTransferred = useMemoryOffTransferred(currentSessionId, currentSession)
  const deepSessionTitle = currentSession?.title ?? "Deep Explorer session"
  const isReader = currentSession?.currentUserRole === "reader"
  // Cursor-style message queue (#2437): admin flag + standard-Explorer-only
  // (v1 excludes Deep/Agent mode) + never for read-only participants.
  const canQueueMessages = isMessageQueueEnabled && !isReader && !enableDeep && !enableAgent

  // Whether steering is available for the CURRENTLY RUNNING turn, independent
  // of whether the message queue is also active. This is the wider signal:
  // it gates the per-bubble "Steer" action on queued messages, which must
  // stay available even while `steerComposerActive` below is suppressed.
  //
  // Durable turns satisfy this gate on the strength of the turn itself, for the
  // same reason the stop gate below does: they run off the HTTP surface, which
  // carries no WS control channel, so `steer_ready` never arrives (#2814).
  // Notes reach them over the durable control plane instead, and their outcome
  // comes back as a `steer_status` replay event that settles the same chip.
  const steerAvailableForRunningTurn =
    steerInjectEnabled &&
    (chatState.steerReady || Boolean(chatState.durableTurn)) &&
    !chatState.cancelPending &&
    (chatState.isLoading || chatState.isStreaming)
  // "Force now" (#2438 scope-widening) is a stage-2 stop-and-redirect action
  // — requestForceRedirect requires stopRedirectEnabled internally, not
  // inject. Gating the button on steerAvailableForRunningTurn above (the
  // inject-based signal) mismatches whenever the two sub-toggles differ:
  // inject-on/stop-off rendered a dead button, inject-off/stop-on hid a
  // working one. This is the same shape as steerAvailableForRunningTurn,
  // keyed on the other sub-toggle.
  //
  // Durable turns (issue #2813) never announce `steer_ready` — they run off
  // the HTTP surface, which carries no WS control channel — so `steerReady`
  // is permanently false for them and this gate could never arm. Stop-and-
  // redirect is nonetheless fully available there, built from durable's own
  // cancel plus the runner's partial persistence, so a live durable turn
  // satisfies this gate in `steerReady`'s place.
  const steerStopAvailableForRunningTurn =
    steerStopRedirectEnabled &&
    (chatState.steerReady || Boolean(chatState.durableTurn)) &&
    !chatState.cancelPending &&
    (chatState.isLoading || chatState.isStreaming)
  // Steer stage-1 (issue #2438) vs message queue (#2437): typing during a
  // running turn queues by default when queueing is available for this
  // session — steering that turn is then an explicit action from the queued
  // bubble (`onSteer` below), not the primary composer send. Only when
  // queueing isn't an option does the composer itself become the steer
  // affordance (ChatFooter renders Send alongside Stop exactly when
  // `showCancelOption && !disableSend`).
  const steerComposerActive = steerAvailableForRunningTurn && !canQueueMessages

  // Get current user's access mode from session participants
  const currentUserOid = SettingsService.getInstance().claims.oid
  const currentUserParticipant = useMemo(() => {
    if (!currentSession?.participants || !currentUserOid) return undefined

    // Get user's groups from settings service
    const userGroups = SettingsService.getInstance().claims.groups || []
    const userGroupsSet = new Set(userGroups)

    // Find participant: check direct user OR group membership
    return currentSession.participants.find(
      (p) =>
        (p.type === "user" && p.oid === currentUserOid) ||
        (p.type === "group" && userGroupsSet.has(p.oid))
    )
  }, [currentSession?.participants, currentUserOid])
  const currentUserAccessMode = currentUserParticipant?.messageAccessMode

  // Get combined answers for display (messages + streamingAnswer if exists)
  const answersForSpreadsheet = useMemo(() => {
    const streaming = chatState.streamingAnswer
    // When an error card is shown, drop a trailing streaming bubble that has no
    // content — otherwise a stalled turn leaves an empty assistant bubble above
    // the error (#2098). Partial content (mid-stream stall) is preserved.
    if (streaming && chatState.error && String(streaming[1]?.content ?? "").trim().length === 0) {
      return chatState.messages
    }
    return streaming ? [...chatState.messages, streaming] : chatState.messages
  }, [chatState.messages, chatState.streamingAnswer, chatState.error])

  // Hoisted from the agent-runs section below (originally at line ~530)
  // because hasStartedDeepSession needs to know whether the current
  // session has a live run — otherwise opening a chat URL whose run
  // started in another tab gets stuck on AgentRunningBanner with no
  // way into the deep workspace until messages hydrate.
  const agentRunsQuery = useAgentRunsQuery()
  const currentSessionAgentRun = useMemo(
    () =>
      currentSessionId
        ? (agentRunsQuery.data?.find((run) => run.session_id === currentSessionId) ?? null)
        : null,
    [agentRunsQuery.data, currentSessionId]
  )
  const isCurrentSessionAgentRunActive =
    currentSessionAgentRun !== null && isActiveAgentRunStatus(currentSessionAgentRun.status)

  // If the user lands on a session whose agent run is still in flight (e.g.
  // from a saved tab or a direct URL), flip Deep mode on so the workspace
  // mounts and the live progress is visible. The use-ai-explorer-chat hook
  // restores Deep when the chat's latest saved answer came from Deep — but
  // that answer isn't there until the run finishes and persists, so the
  // user would otherwise stare at AgentRunningBanner with no way to open
  // the panel.
  //
  // Skip on routes that lock Deep (`disableDeep`): use-ai-explorer-chat forces
  // `enableDeep` back off there, so without this guard the two effects would
  // ping-pong every render.
  const isDeepExplorerEnabled = useDeepExplorerEnabled()
  useEffect(() => {
    if (isDeepExplorerEnabled && isCurrentSessionAgentRunActive && !enableDeep) {
      handleDeepChange(true)
    }
  }, [isDeepExplorerEnabled, isCurrentSessionAgentRunActive, enableDeep, handleDeepChange])

  // Cross-tab live progress: poll agent_run.live while a run is in flight
  // so a second tab populates activity/sources without owning the WebSocket.
  // No-op when there's no active run.
  useDeepLivePoll({
    sessionType,
    sessionId: currentSessionId,
    skipPolling: chatState.isStreaming,
  })

  // Subscribe to the chat-request generation fence so the reattach effect
  // below retries after a late poll/hydration adopt (watching tab that was
  // open before the run started, or a successor turn's new generation).
  const subscribeRequestRegistry = useCallback(
    (listener: () => void) => subscribeChatRequestRegistry(explorerStore, listener),
    [explorerStore]
  )
  const getChatRequestGenerationSnapshot = useCallback(
    () =>
      currentSessionId ? getChatRequestGenerationId(explorerStore, currentSessionId) : undefined,
    [explorerStore, currentSessionId]
  )
  const chatRequestGenerationId = useSyncExternalStore(
    subscribeRequestRegistry,
    getChatRequestGenerationSnapshot,
    getChatRequestGenerationSnapshot
  )

  // Run-broadcaster reattach: when the user lands on a session whose run
  // is still in flight, open a reattach WS so live frames flow at sub-
  // 100ms latency (vs polling's ~3s). Layered on top of polling — if
  // the broadcaster has no slot for this session (process restart, etc),
  // the reattach call is a no-op and polling fills the gap. Guarded
  // against firing while we're already streaming a fresh request.
  // Requires a generation id: reattachToActiveRun no-ops without one, so
  // chatRequestGenerationId is a dep — when poll/hydration later adopts
  // the server generation, this effect re-runs and opens the WS.
  useEffect(() => {
    if (!currentSessionId) return
    if (!isCurrentSessionAgentRunActive) return
    if (chatState.isStreaming) return
    if (!chatRequestGenerationId) return
    reattachToActiveRun(currentSessionId, chatRequestGenerationId)
  }, [
    currentSessionId,
    isCurrentSessionAgentRunActive,
    chatState.isStreaming,
    chatRequestGenerationId,
    reattachToActiveRun,
  ])

  const hasStartedDeepSession =
    answersForSpreadsheet.length > 0 ||
    Boolean(chatState.lastQuestion[0]) ||
    Boolean(chatState.streamingAnswer) ||
    isCurrentSessionAgentRunActive

  // New Deep chrome is active as soon as `/deep` is enabled. The workspace
  // itself waits for actual message/run activity, not just a session shell,
  // so the normal ChatHome examples stay visible while Deep is only armed.
  const deepLayoutMode = useMemo(
    () =>
      getDeepLayoutMode({
        currentSessionId,
        enableDeep,
        hasStartedDeepSession,
        workspaceLayoutEnabled: WORKSPACE_LAYOUT_ENABLED,
      }),
    [currentSessionId, enableDeep, hasStartedDeepSession]
  )
  const useWorkspace = deepLayoutMode.shouldRenderWorkspace
  const hasNewDeepChrome = deepLayoutMode.hasNewDeepChrome
  const canUseLegacyDeepPanel = deepLayoutMode.canUseLegacyDeepPanel
  const currentContentPanelId = useContentSidebarStore(
    (sidebarState) => sidebarState.currentPanel?.id
  )
  const isContextPanelOpen = currentContentPanelId === "ai-explorer-context"
  const selectedFileContextLabels = useMemo(() => getChatSelectedFileItemLabels(t), [t])
  const selectedFileContextItems = useMemo(
    () =>
      buildChatSelectedFileItems({
        availableFiles: files,
        selectedDocuments,
        filesInIndex,
        sessionFileRefs,
        projectId,
        labels: selectedFileContextLabels,
      }),
    [files, filesInIndex, projectId, selectedDocuments, selectedFileContextLabels, sessionFileRefs]
  )
  const getLocalizedM365SourceLabel = useCallback(
    (type: M365Type | null) => (type ? t(`answer.m365_${type}`) : t("answer.m365_sources")),
    [t]
  )
  const answersForReferenceItems = getAnswersForReferenceItems({
    answersForSpreadsheet,
    isContextPanelOpen,
    isStreaming: chatState.isStreaming,
    messages: chatState.messages,
  })
  const latestAnswerReferences = useMemo(() => {
    const latestMessage = answersForReferenceItems[answersForReferenceItems.length - 1]
    if (!latestMessage) return []
    const latestAnswer = latestMessage[1]
    return getAnswerReferenceItems({
      answerText: getResponseContent(latestAnswer),
      dataPoints: getResponseContext(latestAnswer).data_points,
      documents: getDocuments(latestAnswer),
      fallbackDocuments: citationLibraryDocuments,
      getM365SourceLabel: getLocalizedM365SourceLabel,
    })
  }, [answersForReferenceItems, citationLibraryDocuments, getLocalizedM365SourceLabel])

  const { sharedHistoryMarkers } = useSharedHistoryMarkers(
    answersForSpreadsheet,
    currentSession?.participants,
    currentUserParticipant
  )

  // Get current message ID (from latest message in chat)
  const currentMessageId = useMemo(() => {
    if (answersForSpreadsheet.length === 0) return undefined
    const latestMessage = answersForSpreadsheet[answersForSpreadsheet.length - 1]
    if (!latestMessage || !Array.isArray(latestMessage) || latestMessage.length < 2) {
      return undefined
    }
    const response: ChatAppResponse = latestMessage[1]
    return response.messageId ?? getResponseContext(response).message_id ?? undefined
  }, [answersForSpreadsheet])

  // Mark notification as viewed when user opens a shared session
  const markNotificationViewed = useMarkSessionNotificationViewed()
  const markedSessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      currentSessionId &&
      currentSession?.hasUnviewedNotification &&
      markedSessionRef.current !== currentSessionId
    ) {
      markedSessionRef.current = currentSessionId
      markNotificationViewed.mutate(currentSessionId)
    }
    // Reset ref when session changes (so we can mark new session if needed)
    if (
      currentSessionId &&
      markedSessionRef.current !== currentSessionId &&
      !currentSession?.hasUnviewedNotification
    ) {
      markedSessionRef.current = currentSessionId
    }
  }, [currentSessionId, currentSession?.hasUnviewedNotification])

  const { spreadSheetData, spreadSheetChatIndex } = useReadSpreadsheetData({
    files,
    chatIndex: answersForSpreadsheet.length,
    sessionId: currentSessionId,
  })

  // Personalization payload for ChatHome — only fetched while the chat is empty.
  const isChatHomeVisible =
    answersForSpreadsheet.length === 0 &&
    !chatState.lastQuestion[0] &&
    !chatState.isLoading &&
    !chatState.isHydrating &&
    !chatState.streamingAnswer &&
    !chatState.error
  const showM365Option = useM365Enabled()
  // Search of the user's own sources: the switch lives on the chat (server side), so it is
  // loaded per session and survives follow-up turns and reloads.
  const personalSearch = usePersonalSearch(currentSessionId, currentUserOid, explorerStore)
  const translator = useTranslatorTool(
    explorerStore,
    !personalSearch.tainted &&
      routes.some((route) => route.component === "translate" && route.enabled) &&
      SettingsService.getInstance().settings.enabled_tools?.includes("translate_content") === true
  )
  // Locked-down routes can force the plain home via the `disablePersonalizedHome`
  // override — skip the home_context fetch and the M365 consent prompt entirely.
  const showPersonalizedHome = usePersonalizedHomeEnabled()
  // Only fetch and render the enriched home when the user has explicitly approved
  // M365 access. Declining (or never deciding) falls back to the plain home —
  // matches the privacy promise of the consent dialog: "if you don't consent to
  // us using your 365 data, we won't." Without this gate, the home_context
  // endpoint would still be called for declined users, and even after the
  // backend short-circuits Graph for them the helloai.* half would keep
  // rendering the enriched layout.
  const homeContext = useHomeContext(
    isChatHomeVisible && showM365Option && hasM365Approval && showPersonalizedHome
  )
  // Gate the rendered data, not just the fetch: home_context uses a constant
  // query key shared across routes, and React Query keeps cached data for
  // `enabled: false` queries (5-min gcTime). Without `showPersonalizedHome`
  // here, navigating in-SPA from a normal explorer route (cache warm) to a
  // locked route would still render the enriched home from the cache.
  const homeContextData = hasM365Approval && showPersonalizedHome ? homeContext.data : undefined
  // Personalization loading/error notices only make sense when the enriched
  // home is actually in play — suppress them on locked routes (and for users
  // without M365 approval), where the home_context query is disabled.
  const showPersonalizationStatus = showPersonalizedHome && hasM365Approval
  // A 200 response can still carry a degraded payload: `graph.errors` is set
  // per-leg when a Graph fetch genuinely failed (429/401/timeout) rather than
  // succeeding with zero items (see routes/home_context.py). Without this,
  // a throttled calendar looks identical to "you have nothing today" — no
  // notice, no retry. `homeContext.isError` alone only catches HTTP-level
  // failures of the home_context call itself.
  const hasGraphErrors = Boolean(
    homeContextData?.graph.errors && Object.values(homeContextData.graph.errors).some(Boolean)
  )
  // Distinct from `hasGraphErrors`: true only when EVERY Graph signal failed (a
  // real outage), not just one flaky leg. A single failed leg on an otherwise-
  // enriched home is silent (see ChatHome) — the sections that loaded still show
  // what's available, and surfacing every partial hiccup would be noisy. But if
  // ALL of them failed, an active user with real Hello AI history would otherwise
  // see a silently-empty "no meetings today" that's actually an outage — e.g.
  // skipping meeting prep because the calendar looked genuinely clear. That case
  // still needs the notice even though home_context itself returned 200.
  const allGraphLegsFailed = GRAPH_ERROR_LEG_KEYS.every(
    (key) => homeContextData?.graph.errors?.[key]
  )
  const [hasDismissedM365HomePrompt, setHasDismissedM365HomePrompt] = useState(false)
  // The onboarding wizard renders this page underneath itself. Opening the
  // consent dialog while it is up makes the wizard unclickable (#2946), so the
  // prompt waits until the overlay clears — same gate as the What's New dialog.
  const onboardingOverlayActive = useOnboardingOverlayActive()

  useEffect(() => {
    if (
      !shouldAutoPromptM365Consent({
        isChatHomeVisible,
        showM365Option,
        showPersonalizedHome,
        hasM365Approval,
        hasDeclinedM365,
        isM365ApprovalLoading,
        hasDismissedM365HomePrompt,
        onboardingOverlayActive,
      })
    ) {
      return
    }
    setShowM365Info(true)
  }, [
    hasDeclinedM365,
    hasDismissedM365HomePrompt,
    hasM365Approval,
    isChatHomeVisible,
    isM365ApprovalLoading,
    onboardingOverlayActive,
    setShowM365Info,
    showM365Option,
    showPersonalizedHome,
  ])

  // "No thanks" — persists a declined decision and suppresses the home-prompt
  // for this session (the session flag is now redundant since the persisted
  // decision blocks re-prompting too, but kept for the non-persistent dismiss
  // path below).
  const handleM365DialogDecline = useCallback(() => {
    setHasDismissedM365HomePrompt(true)
    handleM365Decline()
  }, [handleM365Decline])

  // Overlay click / Esc — non-persistent. Keep the session flag so the prompt
  // doesn't re-open immediately, but don't record a decision so the user can
  // be re-prompted on a future visit.
  const handleM365DialogDismiss = useCallback(() => {
    setHasDismissedM365HomePrompt(true)
    handleM365Dismiss()
  }, [handleM365Dismiss])

  // Passive typewriter prompts in the chat input. Cycles theme-derived starters
  // (when home_context returns themes) blended with localized generic fallbacks.
  // Freezes on the user's first keystroke via the onFirstUserInput hook below.
  const [hasTypedThisSession, setHasTypedThisSession] = useState(false)
  // Memoize themeLabels by reference so the typewriterPrompts useMemo can
  // depend on the array itself (no JSON / join-key hashing, lint-clean).
  const themeLabels = useMemo<string[]>(
    () => homeContextData?.rendered_starters ?? [],
    [homeContextData?.rendered_starters]
  )
  const typewriterPrompts = useMemo(() => {
    const list: string[] = []
    for (const label of themeLabels.slice(0, 2)) {
      list.push(t("chatHome.starters.tellMeMore", { theme: label }))
      list.push(t("chatHome.starters.whatChanged", { theme: label }))
    }
    const fallbacks = t("chatHome.typewriterPrompts", { returnObjects: true }) as string[] | string
    if (Array.isArray(fallbacks)) {
      for (const f of fallbacks) {
        if (list.length >= 5) break
        list.push(f)
      }
    }
    return list
  }, [themeLabels, t])
  const fallbackPlaceholder = t("pages.common.write_question_here")
  const composerPlaceholder = useRotatingPlaceholder({
    prompts: typewriterPrompts,
    enabled: isChatHomeVisible && !hasTypedThisSession,
    fallback: fallbackPlaceholder,
  })

  const handleContinueChatClick = useCallback(
    (chatId: string, chatSessionType: string) => {
      // Only honor chatSessionType if it resolves to a known route — a stray
      // session_type from another module would otherwise navigate to a
      // non-existent Explorer path.
      const candidate = chatSessionType && getRouteById(chatSessionType) ? chatSessionType : null
      const routeId = candidate ?? sessionType ?? "open"
      void navigate(`${getRouteChatPath(routeId)}/${chatId}`)
    },
    [navigate, sessionType, getRouteChatPath, getRouteById]
  )
  const openPreferencesWith = usePreferencesStore((s) => s.openWith)
  const handleMemoryNudgeClick = useCallback(() => {
    openPreferencesWith("memory")
  }, [openPreferencesWith])

  const handleMeetingPrep = useCallback(
    (meeting: GraphMeeting) => {
      const attendees = meeting.attendees
        .map((a) => a.name)
        .filter((n): n is string => Boolean(n))
        .join(", ")
      const subject = meeting.subject || t("chatHome.meetingHero.untitled", "Untitled meeting")
      const lines = [
        t("chatHome.meetingHero.prompts.prepIntro", {
          subject,
          defaultValue: 'Help me prep for my upcoming meeting: "{{subject}}".',
        }),
        attendees
          ? t("chatHome.meetingHero.prompts.attendees", {
              attendees,
              defaultValue: "Attendees: {{attendees}}.",
            })
          : "",
        meeting.preview
          ? t("chatHome.meetingHero.prompts.agendaPreview", {
              preview: meeting.preview,
              defaultValue: "Agenda preview: {{preview}}",
            })
          : "",
        t("chatHome.meetingHero.prompts.prepInstructions", {
          defaultValue:
            "Summarize what's likely on the table, surface anything I've discussed with these people recently, and suggest 3 talking points.",
        }),
      ].filter(Boolean)
      void handleExampleClicked(lines.join("\n"))
    },
    [handleExampleClicked, t]
  )

  const handleWeeklyAction = useCallback(
    (mode: "wrap" | "plan") => {
      const prompt =
        mode === "wrap"
          ? t("chatHome.weeklyHero.prompts.wrap", {
              defaultValue:
                "Draft a recap of my week. Include the meetings I attended, key topics from my recent chats, and the documents I edited. Keep it tight — 5 bullets.",
            })
          : t("chatHome.weeklyHero.prompts.plan", {
              defaultValue:
                "Help me plan the week ahead. List my upcoming meetings, surface anything that needs prep, and flag deadlines or open threads I should chase.",
            })
      void handleExampleClicked(prompt)
    },
    [handleExampleClicked, t]
  )

  const getMessageKey = useCallback(
    (response: ChatAppResponse, prefix: string, index: number) => {
      const responseId = response.messageId ?? response.context?.message_id
      if (responseId) {
        return responseId
      }
      return `${currentSessionId ?? "session"}-${prefix}-${index}`
    },
    [currentSessionId]
  )

  const isContentSidebarOpen = useContentSidebarStore((sidebarState) => sidebarState.isOpen)
  const openContentPanel = useContentSidebarStore((sidebarState) => sidebarState.openPanel)
  const closeContentPanel = useContentSidebarStore((sidebarState) => sidebarState.closePanel)
  const forceCloseContentPanel = useContentSidebarStore(
    (sidebarState) => sidebarState.forceClosePanel
  )
  const openLocalFilePreview = useCallback((file: File, options?: LocalFilePreviewPanelOptions) => {
    openLocalFilePreviewPanel(file, options)
  }, [])
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareMessageId, setShareMessageId] = useState<string | undefined>()
  const handleManageSharing = useCallback((messageId?: string) => {
    setShareMessageId(messageId)
    setShowShareDialog(true)
  }, [])

  // Phase 5.2 — workspace trace focus.
  //
  // When the user clicks "View trace" on an artifact (in WorkspaceLayout),
  // we record the artifact's `mission_id` here. The activity peek consumes
  // it as `missionFilter` and renders a banner + clear affordance. State
  // lives at the page level because both the workspace layout (writer) and
  // the activity peek (reader) sit underneath it; lifting it any higher
  // (e.g. into the store) is over-engineering for ephemeral UI focus.
  const [traceFocusMission, setTraceFocusMission] = useState<string | null>(null)
  const handleViewTrace = useCallback((missionId: string) => {
    setTraceFocusMission(missionId)
  }, [])
  const handleClearTraceFocus = useCallback(() => {
    setTraceFocusMission(null)
  }, [])

  // Use route-aware feature checks
  const hasAgentModeFeature = useAgentModeEnabled()
  const isPromptsFeatureEnabled = usePromptsEnabled()
  const showWebSearchOption = useWebSearchEnabled()
  const showServiceDeskOption = useServiceDeskEnabled()
  const isVoiceModeEnabled = useVoiceModeEnabled()
  const showNetboxOption = useNetboxEnabled()
  const showXOneOption = useXOneEnabled()
  const showPluginsOption = usePluginsEnabled()
  // The "Manage plugins" entry deep-links to the employee /plugins page, which
  // is a seeded route an admin enables per tenant; without it the submenu
  // still lets users switch installed plugins on and off.
  const pluginsPageAvailable = routes.some((route) => route.path === "/plugins" && route.enabled)
  const handleManagePlugins = useCallback(() => {
    void navigate("/plugins")
  }, [navigate])
  const isDeepExplorerPanelOpen = currentContentPanelId === "deep-explorer"
  const handleRemoveContextFile = useCallback(
    (file: File | string) => {
      void removeExplorerContextFile(file, {
        handleRemoveFile,
        handleRemoveIndexedFile,
      })
    },
    [handleRemoveFile, handleRemoveIndexedFile]
  )

  const openContextPanel = useCallback(() => {
    const hasContextSources =
      !useWorkspace && (selectedFileContextItems.length > 0 || latestAnswerReferences.length > 0)
    const renderContextContent = hasContextSources
      ? (searchQuery: string) => (
          <ChatSelectedFilesContextPanel
            inline
            answerReferences={latestAnswerReferences}
            citationSection={defaultSearchServiceId}
            items={selectedFileContextItems}
            removingFileName={removingFileName}
            searchQuery={searchQuery}
            sessionId={currentSessionId ?? undefined}
            onOpenReference={(url) => window.open(url, "_blank", "noopener,noreferrer")}
            onPreviewFile={openLocalFilePreview}
            onRemoveDocument={handleRemoveDocument}
            onRemoveFile={handleRemoveContextFile}
          />
        )
      : undefined
    const settingsContent = enableAgent ? (
      <ResearchSettingsSidebar
        projectId={projectId}
        renderContextContent={renderContextContent}
        sessionType={sessionType}
      />
    ) : (
      <BaseSettingsSidebar
        projectId={projectId}
        renderContextContent={renderContextContent}
        sessionType={sessionType}
      />
    )

    openContentPanel({
      id: "ai-explorer-context",
      title: t("pages.common.context"),
      content: settingsContent,
      focusable: true,
      closeable: true,
      resizable: true,
    })
  }, [
    currentSessionId,
    defaultSearchServiceId,
    enableAgent,
    handleRemoveDocument,
    handleRemoveContextFile,
    latestAnswerReferences,
    openContentPanel,
    openLocalFilePreview,
    projectId,
    removingFileName,
    selectedFileContextItems,
    sessionType,
    t,
    useWorkspace,
  ])

  const handleDeepModeChange = useCallback(
    (enabled: boolean) => {
      const wasEnabled = enableDeep
      handleDeepChange(enabled)
      // Deep mode no longer auto-opens the side panel — the user pulls the
      // panel up via the header trigger when they want it. We only flip the
      // legacy `deepPanelOpen` flag on disable so an open panel closes with
      // the mode.
      if (!enabled) {
        setDeepPanelOpen(false)
      }
      if (!enabled && isDeepExplorerPanelOpen) {
        forceCloseContentPanel()
      }
      if (enabled && !wasEnabled) {
        showDeepModeOnToast({
          onTurnOff: () => {
            handleDeepChange(false)
            setDeepPanelOpen(false)
            forceCloseContentPanel()
          },
        })
      }
    },
    [
      enableDeep,
      forceCloseContentPanel,
      handleDeepChange,
      isDeepExplorerPanelOpen,
      setDeepPanelOpen,
    ]
  )

  useEffect(() => {
    if (isContextPanelOpen) {
      openContextPanel()
    }
  }, [isContextPanelOpen, openContextPanel])

  // ── Agent runs ──────────────────────────────────────────────────────────
  // Backend owns run state — `agent_run` field on the session document is
  // written by ExplorerDeepApproach as the run progresses. The frontend
  // queries it via `useAgentRunsQuery`; nothing client-side needs to track
  // status. "Run in background" just navigates the user to a fresh chat
  // while the original session keeps streaming server-side.
  // (`agentRunsQuery` / `currentSessionAgentRun` / `isCurrentSessionAgentRunActive`
  // are hoisted earlier so hasStartedDeepSession can reference them.)

  const handleApproveInBackground = useCallback(
    (_extra?: { editedContent?: string; targetSessionId?: string | null }) => {
      // Mirror the regular approve path so the backend run starts; any
      // edited plan content flows through the same decision callback.
      sendPlanDecision("approve", _extra)
      if (currentSessionId) {
        toast.info(
          t("deepExplorer.run_in_background_toast", {
            defaultValue: "Running in background. Find it under Agent runs when it's done.",
          })
        )
      }
      // Free the foreground chat so the user can keep working. The
      // backgrounded session keeps emitting events via WS; the runs
      // panel shows it via the agent_run field that the graph writes.
      handleStartNewChat()
    },
    [currentSessionId, handleStartNewChat, sendPlanDecision, t]
  )

  const openDeepExplorerPanel = useCallback(() => {
    openContentPanel({
      id: "deep-explorer",
      title: <DeepExplorerPanelTitle sessionType={sessionType} />,
      headerActions: <DeepExplorerPanelActions sessionType={sessionType} />,
      content: (
        <DeepExplorerPanelContent
          citationSection={defaultSearchServiceId}
          sessionTitle={deepSessionTitle}
          sessionType={sessionType}
          onApproveInBackground={handleApproveInBackground}
          onAskUserResponse={sendAskUserResponse}
          onPlanDecision={sendPlanDecision}
          onRewindToPlan={rewindToPlan}
          onSendUserNote={sendUserNote}
        />
      ),
      focusable: true,
      closeable: true,
      resizable: true,
      onClose: () => setDeepPanelOpen(false),
    })
  }, [
    defaultSearchServiceId,
    deepSessionTitle,
    handleApproveInBackground,
    openContentPanel,
    rewindToPlan,
    sendAskUserResponse,
    sendPlanDecision,
    sendUserNote,
    sessionType,
    setDeepPanelOpen,
  ])

  // ── Agent runs panel opener (UI-7) ────────────────────────────────────────
  const openResearchRunsPanel = useCallback(() => {
    const action = getResearchRunsPanelAction({
      currentPanelId: currentContentPanelId,
      isContentSidebarOpen,
    })
    if (action === "close") {
      forceCloseContentPanel()
      return
    }

    openContentPanel({
      id: RESEARCH_RUNS_PANEL_ID,
      title: t("deepExplorer.runs_panel_title", {
        defaultValue: "Agent runs",
      }),
      content: (
        <ResearchRunsPanel
          onOpen={(entry) => {
            forceCloseContentPanel()
            // The runs panel only lists deep agent runs, so opening one is
            // an explicit ask to see the deep workspace. Flip the mode +
            // panel state on entry so the user lands on the full panel
            // (plan/activity/sources/deliverables) instead of the lighter
            // AgentRunningBanner that the bare chat shows for in-flight
            // sessions. Without this, running runs read as "Agent is
            // running" only — the rich panel only appeared once the run
            // had finished and messages had hydrated. (Skip the toast
            // shown by handleDeepModeChange — it's surprising in this
            // flow since the user already knows they're entering deep.)
            // Keyed to the run's own chat so the choice survives the navigation
            // below, instead of being undone by that chat's latest-answer
            // restore once its history loads (#3060).
            openSessionInDeepMode(entry.session_id)
            setDeepPanelOpen(true)
            void navigate(buildAgentRunChatPath(getRouteChatPath, entry), {
              state: { forceSessionReload: true },
            })
          }}
        />
      ),
      focusable: true,
      closeable: true,
      desktopWidthPx: RESEARCH_RUNS_PANEL_WIDTH_PX,
      resizable: false,
    })
  }, [
    currentContentPanelId,
    forceCloseContentPanel,
    getRouteChatPath,
    isContentSidebarOpen,
    navigate,
    openContentPanel,
    openSessionInDeepMode,
    setDeepPanelOpen,
    t,
  ])

  useEffect(() => {
    // New Deep chrome owns the activity affordance even before the first
    // session exists, so the legacy ContentSidebar must stay closed.
    if (hasNewDeepChrome) {
      if (isDeepExplorerPanelOpen) {
        forceCloseContentPanel()
      }
      return
    }

    if ((!enableDeep || !deepPanelOpen) && isDeepExplorerPanelOpen) {
      forceCloseContentPanel()
      return
    }

    if (enableDeep && deepPanelOpen && !isDeepExplorerPanelOpen) {
      openDeepExplorerPanel()
    }
  }, [
    deepPanelOpen,
    enableDeep,
    forceCloseContentPanel,
    isDeepExplorerPanelOpen,
    openDeepExplorerPanel,
    hasNewDeepChrome,
  ])

  // When the chat session changes while the deep panel is already open,
  // re-call openDeepExplorerPanel so the panel's React element is rebuilt
  // with fresh closures (sessionTitle, plan-decision callbacks). Without
  // this the panel keeps showing the previous session's bindings — visible
  // as the old plan/title leaking into a new chat.
  const lastSessionForDeepPanelRef = useRef<string | null>(currentSessionId)
  useEffect(() => {
    if (lastSessionForDeepPanelRef.current !== currentSessionId) {
      lastSessionForDeepPanelRef.current = currentSessionId
      // New Deep chrome does not use the legacy panel, so skip the session
      // rebuild reopen even before the workspace has a session id.
      if (hasNewDeepChrome) return
      if (enableDeep && deepPanelOpen && isDeepExplorerPanelOpen) {
        openDeepExplorerPanel()
      }
    }
  }, [
    currentSessionId,
    deepPanelOpen,
    enableDeep,
    isDeepExplorerPanelOpen,
    openDeepExplorerPanel,
    hasNewDeepChrome,
  ])

  const sidebarTriggerLabel = useMemo(() => {
    if (isContentSidebarOpen) {
      return t("analysispanel.close_sidebar", { defaultValue: "Close sidebar" })
    }

    if (enableDeep && canUseLegacyDeepPanel) {
      return t("deepExplorer.open_panel", { defaultValue: "Open Deep Explorer" })
    }

    return t("analysispanel.open_context", { defaultValue: "Open Context" })
  }, [canUseLegacyDeepPanel, enableDeep, isContentSidebarOpen, t])

  const toggleSharedSidebarPanel = useCallback(() => {
    if (isContentSidebarOpen) {
      closeContentPanel()
      return
    }

    const target = getSharedSidebarTarget({ canUseLegacyDeepPanel, enableDeep })
    if (target === "legacyDeep") {
      setDeepPanelOpen(true)
      openDeepExplorerPanel()
      return
    }

    openContextPanel()
  }, [
    closeContentPanel,
    canUseLegacyDeepPanel,
    enableDeep,
    isContentSidebarOpen,
    openContextPanel,
    openDeepExplorerPanel,
    setDeepPanelOpen,
  ])

  // Voice mode consent modal state
  const [showVoiceModeInfo, setShowVoiceModeInfo] = useState(false)
  const toggleVoiceModeRef = useRef<(() => void) | null>(null)

  // Callback to check voice mode consent before starting
  const handleBeforeVoiceModeStart = useCallback((): boolean => {
    if (hasVoiceInternalDataConsent()) {
      return true
    }
    // Show modal and prevent voice mode from starting
    setShowVoiceModeInfo(true)
    return false
  }, [])

  // Names the provider in the consent modal's copy.
  const voiceEngine = useVoiceEngine()

  // Called when user accepts the voice mode consent modal
  const handleVoiceModeAccept = useCallback(() => {
    // Start voice mode after consent is given
    toggleVoiceModeRef.current?.()
  }, [])

  // Store the toggle function from ChatFooter
  const handleGetToggleVoiceMode = useCallback((toggleFn: () => void) => {
    toggleVoiceModeRef.current = toggleFn
  }, [])

  const handleStartVoiceModeFromTools = useCallback(() => {
    const canStart = handleBeforeVoiceModeStart()
    if (!canStart) return
    toggleVoiceModeRef.current?.()
  }, [handleBeforeVoiceModeStart])

  // State for exposing setQuestion function from Chat.Footer
  const [setQuestionFunction, setSetQuestionFunction] = useState<((text: string) => void) | null>(
    null
  )

  // State for "Ask about" pill text
  const [askAboutText, setAskAboutText] = useState<string | null>(null)
  const handleSetAskAboutText = useCallback((text: string) => {
    setAskAboutText(text)
  }, [])
  const handleClearAskAboutText = useCallback(() => {
    setAskAboutText(null)
  }, [])

  // Handle pending message from "break out to new chat"
  const pendingMessage = usePendingMessageStore((state) => state.pendingMessage)
  const clearPendingMessage = usePendingMessageStore((state) => state.clearPendingMessage)

  // Handle pending workflow from sidebar click
  const pendingWorkflow = usePendingWorkflowStore((state) => state.pendingWorkflow)
  const clearPendingWorkflow = usePendingWorkflowStore((state) => state.clearPendingWorkflow)
  const handledPendingWorkflowIdRef = useRef<string | null>(null)
  const [workflowToRun, setWorkflowToRun] = useState<WorkflowItem | null>(null)

  // Context menu actions for break out functionality
  const { handleBreakOutChat: contextMenuBreakOut } = useContextMenuActions({
    currentSessionId,
    onSendQuestion: handleSendQuestion,
    sessionType: sessionType,
    projectId,
  })

  // Handler for breaking out from AI response button
  const handleBreakOutFromAIResponse = useCallback(
    async (content: string) => {
      try {
        logger.info("Break out button clicked", { contentLength: content.length })
        await contextMenuBreakOut(content, "user")
      } catch (error) {
        logger.error("Break out button failed", error as Error)
        toast.error(t("dash.chat.create_error"))
      }
    },
    [contextMenuBreakOut, t]
  )

  useEffect(() => {
    // Wait for the new chat to be fully loaded
    // Check if we have a pending message AND we're in a different session
    if (
      pendingMessage &&
      (currentSessionId !== pendingMessage.sourceSessionId || currentSessionId === null)
    ) {
      // Small delay to ensure the chat is fully rendered
      const timer = setTimeout(() => {
        if (pendingMessage.messageType === "assistant") {
          // For assistant messages, inject directly into chat state
          logger.info("Processing pending assistant message", {
            textLength: pendingMessage.text.length,
            sourceSessionId: pendingMessage.sourceSessionId,
          })
          const statusLine = pendingMessage.sourceSessionId
            ? t("dash.chat.breakout_context_link", {
                title: pendingMessage.sourceSessionTitle || t("dash.chat.previous_chat"),
                url: `/explorer/chat/${pendingMessage.sourceSessionId}`,
              })
            : t("dash.chat.breakout_context_previous")
          const displayContent = statusLine + pendingMessage.text

          // Inject as an AI response
          const updateChatState = explorerStore.getState().changeChatState
          // Read chatState inside changeChatState to avoid overwriting concurrent updates
          const currentSessId = explorerStore.getState().currentSessionId
          const currentMessages = currentSessId
            ? (explorerStore.getState().sessionStates[currentSessId]?.messages ?? [])
            : []
          updateChatState({
            messages: [
              ...currentMessages,
              [
                "", // Empty user message
                {
                  content: displayContent,
                  context: {
                    data_points: [],
                    followup_questions: null,
                    thoughts: [],
                  },
                  session_state: null,
                  completed: true,
                },
              ],
            ],
          })

          toast.info(t("dash.chat.created"), {
            description: t("dash.chat.breakout_created", {
              title: pendingMessage.sourceSessionTitle || t("dash.chat.previous_chat"),
            }),
          })
        } else {
          // For user messages, populate the chat input if available
          if (setQuestionFunction) {
            setQuestionFunction(pendingMessage.text)
            if (pendingMessage.sourceSessionTitle) {
              toast.info(t("dash.chat.created"), {
                description: t("dash.chat.created_from", {
                  title: pendingMessage.sourceSessionTitle,
                }),
              })
            }
          }
        }
        // Clear the pending message
        clearPendingMessage()
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [pendingMessage, setQuestionFunction, currentSessionId, clearPendingMessage, t])

  const runWorkflowViaApi = useCallback(
    async (
      workflow: WorkflowItem,
      question = "",
      userInputData?: Record<string, string | File[]>
    ) => {
      const userText = getWorkflowRunChatQuestion(workflow, question)
      // Only the text (string) fields are JSON-safe for the run POST; file fields
      // route through the chat path above. This branch only runs when no files.
      const textInputData = userInputData
        ? Object.fromEntries(
            Object.entries(userInputData).filter(
              (entry): entry is [string, string] => typeof entry[1] === "string"
            )
          )
        : undefined
      const hasTextInputData = !!textInputData && Object.keys(textInputData).length > 0
      // Always creates a brand-new session and targets it explicitly — never reads
      // ambient "current session" state. Used both for the initial session (when
      // none exists yet) and as a persist-time fallback if that initial attempt
      // failed transiently, so a flow-run result is never silently dropped. Because
      // it never falls back to "whatever session is current now", it can't
      // reintroduce the wrong-session bug (writing a run into a session the run
      // didn't originate in).
      const createRunSession = async (chatService: ChatService): Promise<string | null> => {
        const newSession = await chatService.createSession(
          sessionType,
          userText.slice(0, 50),
          undefined,
          false,
          undefined,
          projectId
        )
        if (!newSession?.id) return null

        const sessionHistoryKey = queryKeys.sessionHistory(sessionType, projectId)
        explorerStore.getState().setCurrentSessionId(newSession.id)
        explorerStore.getState().initSession(newSession.id)
        await queryClient.cancelQueries({ queryKey: sessionHistoryKey })
        queryClient.setQueryData(
          sessionHistoryKey,
          (oldSessions: ChatSessionModel[] | undefined) => {
            const sessions = oldSessions ?? []
            const remainingSessions = sessions.filter(
              (existingSession) => existingSession?.id !== newSession.id
            )
            return [newSession, ...remainingSessions]
          }
        )
        void navigate(`${getRouteChatPath(sessionType)}/${newSession.id}`)
        return newSession.id
      }

      const ensureRunSession = async (chatService: ChatService): Promise<string | null> => {
        const currentSessId = explorerStore.getState().currentSessionId
        if (currentSessId) return currentSessId

        return createRunSession(chatService)
      }

      // Persist-time fallback wrapper: createRunSession rejects on a real
      // network/5xx session-create failure (it only returns null when the created
      // session lacks an id). Used from the persist helpers below, that rejection
      // would escape past the persistence boundary into the outer catch and
      // discard an already-completed run result. Swallow it here and surface null
      // so callers treat it as "result not saved" instead of throwing.
      const createRunSessionOrNull = async (chatService: ChatService): Promise<string | null> => {
        try {
          return await createRunSession(chatService)
        } catch (error) {
          logger.warn("Failed to create fallback chat session for flow run", error as Error)
          return null
        }
      }

      // Runs can take minutes (the POST below is synchronous on the backend). The
      // session the run started in may not be the current session by the time it
      // resolves, so every downstream write targets the captured `sessionId`
      // directly via `updateSession` rather than `changeChatState` (which always
      // writes to whatever session is current *now*), and navigation only fires
      // if the user hasn't since moved to another session.
      const resetRunStreamingState = (sessionId: string) => {
        explorerStore.getState().updateSession(sessionId, {
          streamingAnswer: null,
          isLoading: false,
          isStreaming: false,
        })
      }

      const navigateIfStillCurrent = (sessionId: string) => {
        if (explorerStore.getState().currentSessionId === sessionId) {
          void navigate(`${getRouteChatPath(sessionType)}/${sessionId}`, { replace: true })
        }
      }

      const appendChatResponse = async (response: ChatAppResponse, sessionId: string | null) => {
        const chatService = ChatService.getInstance()
        // sessionId is normally captured once up front (see runSessionId below). If
        // that initial ensure failed transiently, retry it here rather than
        // dropping the result — but only by creating a fresh session, never by
        // falling back to whatever session happens to be current now.
        const targetSessionId = sessionId ?? (await createRunSessionOrNull(chatService))
        if (!targetSessionId) {
          toast.error(t("workflowLanding.toast.runResultNotSaved"), {
            description: t("workflowLanding.toast.runResultNotSavedNoSession"),
          })
          return false
        }

        try {
          await chatService.saveWorkflowRunMessage(targetSessionId, userText, response)
        } catch (error) {
          logger.warn("Failed to persist workflow run chat message", error as Error)
          toast.error(t("workflowLanding.toast.runResultNotSaved"), {
            description: t("workflowLanding.toast.runResultNotSavedInspectHistory"),
          })
          resetRunStreamingState(targetSessionId)
          return false
        }

        const currentMessages =
          explorerStore.getState().sessionStates[targetSessionId]?.messages ?? []
        explorerStore.getState().updateSession(targetSessionId, {
          messages: [...currentMessages, [userText, response]],
          streamingAnswer: null,
          isLoading: false,
          isStreaming: false,
        })
        navigateIfStillCurrent(targetSessionId)
        return true
      }

      const appendRunMessage = async (run: FlowRun, sessionId: string | null) => {
        const chatService = ChatService.getInstance()
        // Same transient-failure fallback as appendChatResponse above.
        const targetSessionId = sessionId ?? (await createRunSessionOrNull(chatService))
        if (!targetSessionId) {
          toast.error(t("workflowLanding.toast.runResultNotSaved"), {
            description: t("workflowLanding.toast.runResultNotSavedNoSession"),
          })
          return false
        }

        try {
          const runResponse = await persistWorkflowRunStatusChatResponse({
            sessionId: targetSessionId,
            userText,
            run,
            saveWorkflowRunMessage: chatService.saveWorkflowRunMessage.bind(chatService),
          })
          const currentMessages =
            explorerStore.getState().sessionStates[targetSessionId]?.messages ?? []
          explorerStore.getState().updateSession(targetSessionId, {
            messages: [...currentMessages, [userText, runResponse]],
            streamingAnswer: null,
            isLoading: false,
            isStreaming: false,
          })
          navigateIfStillCurrent(targetSessionId)
          return true
        } catch (error) {
          logger.warn("Failed to persist workflow run chat message", error as Error)
          toast.error(t("workflowLanding.toast.runResultNotSaved"), {
            description: t("workflowLanding.toast.runResultNotSavedInspectHistory"),
          })
          resetRunStreamingState(targetSessionId)
          return false
        }
      }

      const showPendingRunMessage = async (): Promise<string | null> => {
        const chatService = ChatService.getInstance()
        const currentSessId = await ensureRunSession(chatService)
        if (!currentSessId) return null

        markUncancellableFlowRun(currentSessId)
        const pendingResponse = createWorkflowRunPendingChatResponse(workflow)
        explorerStore.getState().updateSession(currentSessId, {
          streamingAnswer: [userText, pendingResponse],
          isLoading: true,
          isStreaming: true,
          canContinue: false,
        })
        navigateIfStillCurrent(currentSessId)
        return currentSessId
      }

      // Captured once, up front, so the long-running POST below always resolves
      // against the session the run actually started in — not whatever session
      // happens to be current when the response comes back.
      let runSessionId: string | null = null

      // Mirrors the chat-WS path (use-chat.ts) so the sidebar Run button's
      // disable/spinner logic also covers this REST path, and a second click
      // during a multi-minute synchronous run can't start a duplicate run.
      const workflowRunOwnerToken = Symbol("workflow-rest-run")
      useWorkflowRunStore.getState().setRunningWorkflowId(workflow.id, workflowRunOwnerToken)

      try {
        runSessionId = await showPendingRunMessage()
        const run = (await api.post(`/explorer/workflow/${workflow.id}/run`, {
          question,
          ...(hasTextInputData ? { user_input_data: textInputData } : {}),
        })) as unknown as FlowRun

        if (run.status === "failed") {
          await appendRunMessage(run, runSessionId)
          toast.error(t("workflowLanding.toast.runFailed"), {
            description: run.error ?? workflow.title,
          })
          return
        }

        if (run.status === "paused") {
          await appendRunMessage(run, runSessionId)
          toast.info(t("workflowLanding.toast.runPaused"), {
            description: workflow.title,
          })
          return
        }

        if (run.status === "queued" || run.status === "running") {
          await appendRunMessage(run, runSessionId)
          toast.info(
            run.status === "queued"
              ? t("workflowLanding.toast.runQueued")
              : t("workflowLanding.toast.runRunning"),
            {
              description: t("workflowLanding.toast.runInspectProgress"),
            }
          )
          return
        }

        await appendRunMessage(run, runSessionId)

        toast.success(t("workflowLanding.toast.runCompleted"), {
          description: workflow.title,
        })
      } catch (error) {
        const failedRun = getWorkflowRunFromApiError(error)
        if (failedRun) {
          await appendRunMessage(failedRun, runSessionId)
        } else {
          await appendChatResponse(
            createWorkflowRunRequestErrorChatResponse({
              workflowId: workflow.id,
              workflowTitle: workflow.title,
              error,
            }),
            runSessionId
          )
        }
        toast.error(t("workflowLanding.toast.runFailed"), {
          description: error instanceof Error ? error.message : workflow.title,
        })
      } finally {
        if (runSessionId) clearUncancellableFlowRun(runSessionId)
        useWorkflowRunStore.getState().clearRunningWorkflowId(workflowRunOwnerToken)
      }
    },
    [
      clearUncancellableFlowRun,
      explorerStore,
      getRouteChatPath,
      markUncancellableFlowRun,
      navigate,
      projectId,
      queryClient,
      sessionType,
      t,
    ]
  )

  // Handle pending workflow from sidebar - show input dialog or run immediately
  useEffect(() => {
    const consumption = getPendingWorkflowConsumption(
      pendingWorkflow?.id,
      handledPendingWorkflowIdRef.current
    )
    handledPendingWorkflowIdRef.current = consumption.nextHandledWorkflowId

    if (!pendingWorkflow || !consumption.shouldConsume) return
    clearPendingWorkflow()

    if (workflowRequiresInput(pendingWorkflow)) {
      // Show the input dialog
      setWorkflowToRun(pendingWorkflow)
    } else {
      void runWorkflowViaApi(pendingWorkflow)
    }
  }, [pendingWorkflow, clearPendingWorkflow, runWorkflowViaApi])

  // Handle workflow run from input dialog
  const handleWorkflowRun = useCallback(
    async (data: {
      question: string
      files?: File[]
      user_input_data?: Record<string, string | File[]>
    }) => {
      if (!workflowToRun) return

      // Only text (string) START fields ride in user_input_data JSON; file
      // fields go through the upload pipeline below. Never drop the text fields
      // just because files are present (#2176).
      const textInputData = data.user_input_data
        ? Object.fromEntries(
            Object.entries(data.user_input_data).filter(
              (entry): entry is [string, string] => typeof entry[1] === "string"
            )
          )
        : undefined
      const hasTextInputData = !!textInputData && Object.keys(textInputData).length > 0

      if (data.files && data.files.length > 0) {
        handleAddFiles(data.files)
        await handleSendQuestion({
          question: data.question,
          agentId: workflowToRun.id,
          files: data.files,
          ...(hasTextInputData ? { user_input_data: textInputData } : {}),
        })
      } else {
        await runWorkflowViaApi(workflowToRun, data.question, data.user_input_data)
      }

      // Close the dialog
      setWorkflowToRun(null)
    },
    [workflowToRun, handleAddFiles, handleSendQuestion, runWorkflowViaApi]
  )

  // Memoize expensive computations
  const maxTokens = useMemo(
    () => selectedReasoningModel?.input_token_limit ?? 0,
    [selectedReasoningModel]
  )

  const dropzoneOptions = useMemo(() => {
    // Choose base file types based on Azure OpenAI Responses API usage
    const baseTypes = useResponsesFileSearch ? azureOpenAIAcceptedFileTypes : acceptedFileTypes
    const baseTypesNoImages = useResponsesFileSearch
      ? azureOpenAIAcceptedFileTypesWithoutImages
      : acceptedFileTypesWithoutImages

    return {
      accept:
        isReasoningModelActive && !(selectedReasoningModel?.feature_flags?.supports_images ?? true)
          ? baseTypesNoImages
          : baseTypes,
    }
  }, [isReasoningModelActive, selectedReasoningModel, useResponsesFileSearch])

  // Stable callback for onSetQuestion
  const handleSetQuestion = useCallback((setter: (text: string) => void) => {
    setSetQuestionFunction(() => setter) // Use functional update
  }, [])

  const handleTryAgainAction = useCallback(
    (question: string) => handleSendQuestion({ question }),
    [handleSendQuestion]
  )

  const handleSendFollowUp = useCallback(
    async ({ question, forceWebSearch }: { question: string; forceWebSearch?: boolean }) => {
      if (!question || !question.trim()) return
      const effectiveForceWebSearch = showWebSearchOption ? forceWebSearch : false
      if (effectiveForceWebSearch) {
        handleWebSearchChange(true)
      }
      await handleSendQuestion({ question, forceWebSearch: effectiveForceWebSearch })
    },
    [handleSendQuestion, handleWebSearchChange, showWebSearchOption]
  )

  // Ref to hold the sendSessionContext function from VoiceInput
  const sendSessionContextRef = useRef<((sessionId: string) => void) | null>(null)

  // Tracks edit_in_canvas dispatches that are still in flight per artifact id.
  // Without this guard a rapid double-click creates two real documents via
  // createDocumentApi (each call mutates server state), leaving an orphan
  // duplicate. The set is mutated synchronously inside the click handler so
  // a second click before the first await resolves is a no-op.
  const editInCanvasInFlightRef = useRef<Set<string>>(new Set())

  // Callback to receive sendSessionContext function from VoiceInput
  const handleGetSendSessionContext = useCallback((sendFn: (sessionId: string) => void) => {
    sendSessionContextRef.current = sendFn
  }, [])

  // Handler for voice message transcripts
  const handleVoiceMessage = useCallback(
    async (userText: string, response: ChatAppResponse, backendSessionId?: string) => {
      const chatService = ChatService.getInstance()
      let currentSessId = explorerStore.getState().currentSessionId

      // The realtime backend creates a chat container in the background as
      // soon as the first turn starts so artifact tools work on that turn.
      // Adopt that exact id before persistence; creating a second session here
      // would split the transcript and generated artifacts across two chats.
      if (!currentSessId && backendSessionId) {
        currentSessId = backendSessionId
        explorerStore.getState().setCurrentSessionId(backendSessionId)
        explorerStore.getState().initSession(backendSessionId)
        window.history.pushState({}, "", `${voiceSessionRoutePrefix}/${backendSessionId}`)
        logger.info("Adopted backend-created voice chat session", {
          sessionId: backendSessionId,
        })
      }

      // Create a session if none exists
      if (!currentSessId) {
        try {
          const titleForSession = userText.slice(0, 50)
          const newSession = await chatService.createSession(
            sessionType,
            titleForSession,
            undefined,
            false,
            undefined,
            projectId,
            "voice" // Icon type for voice mode sessions
          )
          if (newSession?.id) {
            currentSessId = newSession.id
            explorerStore.getState().setCurrentSessionId(newSession.id)
            explorerStore.getState().initSession(newSession.id)
            // Navigate to the new session
            window.history.pushState({}, "", `${voiceSessionRoutePrefix}/${newSession.id}`)
            logger.info("Created new voice chat session", { sessionId: newSession.id })

            // CRITICAL: Send session_id to backend via WebSocket immediately
            // This ensures the backend saves the conversation to this session, not a new one
            if (sendSessionContextRef.current) {
              sendSessionContextRef.current(newSession.id)
            }
          }
        } catch (error) {
          logger.error("Failed to create voice chat session", error as Error)
          return
        }
      }

      // Add the message to the chat store (backend saves via WebSocket)
      const changeChatState = explorerStore.getState().changeChatState
      const currentMessages = currentSessId
        ? (explorerStore.getState().sessionStates[currentSessId]?.messages ?? [])
        : []
      changeChatState({
        messages: [...currentMessages, [userText, response]],
      })
    },
    [explorerStore, projectId, sessionType, voiceSessionRoutePrefix]
  )

  // Handler for streaming voice message (real-time text display)
  const handleStreamingVoiceMessage = useCallback(
    (userText: string, response: ChatAppResponse) => {
      const changeChatState = explorerStore.getState().changeChatState
      changeChatState({
        streamingAnswer: [userText, response],
        isStreaming: true,
      })
    },
    [explorerStore]
  )

  // Handler to clear streaming state when voice response is complete
  const handleStreamingComplete = useCallback(() => {
    const changeChatState = explorerStore.getState().changeChatState
    changeChatState({
      streamingAnswer: null,
      isStreaming: false,
    })
  }, [explorerStore])

  const handleSwitchModelAction = useCallback(
    (modelId: string) => {
      const model = availableGptModels.find((item) => item.id === modelId)
      if (model) {
        // Session override, not handleGptModelChange — the latter only
        // persists the now-unused gptModel setting; effectiveModel won't
        // pick it up.
        setModelOverride(model.id)
      } else {
        openContextPanel()
      }
    },
    [availableGptModels, setModelOverride, openContextPanel]
  )

  // Callback to refresh messages after update
  const handleMessageUpdate = useCallback(async () => {
    if (!currentSessionId) return

    try {
      const chatService = ChatService.getInstance()
      const messages = await chatService.getMessages(currentSessionId)

      // Helper to add message ID to response
      const addMessageId = (response: ChatAppResponse, messageId: string): ChatAppResponse => {
        return {
          ...response,
          messageId, // Add to top level for Canvas editing
          context: {
            ...response.context,
            message_id: messageId, // Also in context for backward compatibility
          },
        }
      }

      // Helper to ensure activities array exists
      const ensureActivitiesArray = (response: ChatAppResponse) => {
        if (!response.activities) {
          return { ...response, activities: [] }
        }
        return response
      }

      const answers: [string, ChatAppResponse][] = messages.reduce(
        (acc, message) => {
          try {
            const content = message.content as unknown as
              | [string, ChatAppResponse]
              | [string, ChatAppResponse][]

            if (Array.isArray(content)) {
              if (content.length > 0) {
                if (
                  content.length === 2 &&
                  typeof content[0] === "string" &&
                  typeof content[1] === "object"
                ) {
                  // Single interaction format: [question, response]
                  const responseObj = content[1] as ChatAppResponse
                  const responseWithId = addMessageId(responseObj, message.id)
                  return acc.concat([[content[0], ensureActivitiesArray(responseWithId)]])
                } else {
                  // Array of interactions format
                  const processedContent = content.map((item) => {
                    if (Array.isArray(item)) {
                      const responseWithId = addMessageId(item[1] as ChatAppResponse, message.id)
                      return [item[0], ensureActivitiesArray(responseWithId)]
                    }
                    return item
                  })
                  return acc.concat(processedContent as [string, ChatAppResponse][])
                }
              }
            }
            return acc
          } catch {
            // Silently handle errors during message processing
            return acc
          }
        },
        [] as [string, ChatAppResponse][]
      )

      // Update chat state with refreshed messages
      const changeChatState = explorerStore.getState().changeChatState
      changeChatState({ messages: answers })
    } catch {
      // Silently handle errors during refresh
    }
  }, [currentSessionId])

  // Callback to handle sharing a specific message
  const handleShareMessage = useCallback((messageId?: string) => {
    setShareMessageId(messageId)
    setShowShareDialog(true)
  }, [])

  // Note: Session opening is now fully handled by useSessionSync hook in use-chat-history.ts
  // which properly loads the full session data including file_refs.
  // Previously, this effect was prematurely opening sessions with minimal data,
  // preventing handleSelectSession from being called with complete session data.

  // Reset citations when projectId context changes (e.g., navigating from project chat to regular chat)
  useEffect(() => {
    navigatorStore.getState().setCitations([])
    navigatorStore.getState().setActiveCitations([])
  }, [projectId, navigatorStore])

  // Global keyboard shortcuts for Deep mode
  // Cmd/Ctrl+Shift+D — toggle Deep mode
  // Cmd/Ctrl+Shift+P — open plan preview (when deep mode on + plan proposed)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey
      if (!isMod || !e.shiftKey) return

      if (e.key === "D" || e.key === "d") {
        e.preventDefault()
        handleDeepModeChange(!enableDeep)
      } else if ((e.key === "P" || e.key === "p") && enableDeep) {
        e.preventDefault()
        // Re-open plan modal if we have proposed todos
        const sid = explorerStore.getState().currentSessionId
        if (!sid) return
        const deepState = getDeepStateForSession(explorerStore.getState(), sid)
        if (deepState.proposedTodos.length > 0) {
          explorerStore.getState().setDeepPlanProposed(sid, true, deepState.proposedTodos)
        }
      } else if ((e.key === "B" || e.key === "b") && enableDeep) {
        e.preventDefault()
        toggleSharedSidebarPanel()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [enableDeep, explorerStore, handleDeepModeChange, toggleSharedSidebarPanel])

  const showHighReasoning =
    availableGptModels.some((model: GPTModelItem) => model.id === "gpt-5_eu") && !enableAgent
  const showReasoningMode = showHighReasoning

  // Function to apply prompt settings when a prompt is selected
  const applyPromptSettings = useCallback(
    (prompt: PromptLibraryItem) => {
      // Apply web search setting
      if (prompt.defaultWebSearch !== undefined) {
        handleWebSearchChange(prompt.defaultWebSearch)
      }

      // Apply trusted sources
      if (
        prompt.useTrustedSources &&
        prompt.trustedSourceUrls &&
        prompt.trustedSourceUrls.length > 0
      ) {
        setSourceUrls(prompt.trustedSourceUrls.join("\n"))
        if (prompt.trustedMode) {
          setTrustedMode(prompt.trustedMode)
        }
      }

      // Apply library settings
      if (prompt.enabledIndexIds && prompt.enabledIndexIds.length > 0) {
        const availableLibraryIds = Object.keys(enabledLibraries)
        const enabledIds = prompt.enabledIndexIds

        availableLibraryIds.forEach((libraryId) => {
          const isCurrentlyEnabled = enabledLibraries[libraryId].checked
          const shouldBeEnabled = enabledIds.includes(libraryId)

          if (isCurrentlyEnabled !== shouldBeEnabled) {
            toggleLibraryChecked(libraryId)
          }
        })
      }

      // Apply AI model setting. Sets the session override (the mechanism
      // effectiveModel actually honors) rather than handleGptModelChange,
      // which only persists the now-unused gptModel setting.
      if (prompt.defaultModelId) {
        const model = availableGptModels.find((m) => m.id === prompt.defaultModelId)
        if (model) {
          setModelOverride(model.id)

          // Apply high reasoning setting with the new model. When explicitly
          // disabled, this resolves to the "auto" reasoning mode, which
          // unconditionally clears modelOverride — re-apply the prompt's
          // model override after so it isn't wiped. When enabled, Big
          // Brain's own preset lock intentionally wins over the prompt's
          // model, so that path is left alone.
          if (prompt.defaultHighReasoning !== undefined) {
            handleHighReasoningChange(prompt.defaultHighReasoning, model)
            if (prompt.defaultHighReasoning === false) {
              setModelOverride(model.id)
            }
          }
        }
      } else if (prompt.defaultHighReasoning !== undefined) {
        // No model change, use current model
        handleHighReasoningChange(prompt.defaultHighReasoning)
      }
    },
    [
      handleWebSearchChange,
      setSourceUrls,
      setTrustedMode,
      enabledLibraries,
      toggleLibraryChecked,
      handleHighReasoningChange,
      availableGptModels,
      setModelOverride,
    ]
  )

  const showStartResearchButton =
    hasAgentModeFeature &&
    enableAgent &&
    hasLatestPlan &&
    !chatState.isLoading &&
    !chatState.isStreaming

  // Apply project defaults when project changes
  useProjectDefaults({
    activeProject,
    allowWebSearch: showWebSearchOption,
    enableWebSearch,
    handleWebSearchChange,
    availableModels: availableGptModels,
    currentGptModel: dbGptModel,
    modelSettingsHydrated,
    handleGptModelChange,
    sourceUrls: sourceUrlsInput,
    setSourceUrls,
    trustedMode,
    setTrustedMode,
  })

  // ──────────────────────────────────────────────────────────────────────
  // Render helpers — extracted from the JSX below so the same conversation
  // and composer markup can be slotted into either the legacy chat layout
  // or the new WorkspaceLayout (Phase 4.3b) without duplication. They close
  // over component state by design; they take no arguments. The whole
  // `<Chat.Content>` / `<Chat.Footer>` element (props + children) is kept
  // intact inside each helper so that the legacy and Workspace branches
  // share the exact same prop set and behavior — the alternative (extract
  // only children, re-pass ~50 Footer props in two places) would invite
  // drift between branches.
  // ──────────────────────────────────────────────────────────────────────
  const renderConversationContent = () => (
    <Chat.Content>
      <MemoryOffTransferredNotice transferred={memoryOffTransferred} />
      {answersForSpreadsheet.length === 0 &&
        !chatState.lastQuestion[0] &&
        !chatState.isLoading &&
        !chatState.isHydrating &&
        !chatState.streamingAnswer &&
        !aborted &&
        !chatState.error &&
        !isCurrentSessionAgentRunActive && (
          <ChatHome
            exampleQuestions={exampleQuestions}
            isPersonalizationLoading={showPersonalizationStatus && homeContext.isLoading}
            personalization={homeContextData}
            isPersonalizationError={
              showPersonalizationStatus && (homeContext.isError || hasGraphErrors)
            }
            isPersonalizationSystemicError={
              showPersonalizationStatus && (homeContext.isError || allGraphLegsFailed)
            }
            onContinueChatClick={handleContinueChatClick}
            onExampleQuestionClick={handleExampleClicked}
            onMeetingPrep={handleMeetingPrep}
            onMemoryNudgeClick={handleMemoryNudgeClick}
            onWeeklyAction={handleWeeklyAction}
            onRetryPersonalization={() => {
              void (async () => {
                try {
                  // Drop the backend's in-memory Graph cache first — without this,
                  // a plain refetch() would just re-serve the same 429/timeout
                  // negative-cached result for up to _EMPTY_RESULT_CACHE_TTL_SECONDS.
                  await refreshGraphApi(SettingsService.getInstance().token)
                } catch {
                  // Best-effort: still refetch below even if the cache-invalidate
                  // call itself failed — worst case it re-surfaces the same error.
                }
                void homeContext.refetch()
              })()
            }}
          />
        )}
      {/* Live "agent is running" banner — visible when the user
          re-opens a session whose backgrounded run is still
          streaming. Replaces the empty-chat state until the run
          terminates and the assistant response is persisted. */}
      {isCurrentSessionAgentRunActive &&
        currentSessionAgentRun &&
        !chatState.streamingAnswer &&
        answersForSpreadsheet.length === 0 && (
          <AgentRunningBanner agentRun={currentSessionAgentRun} sessionType={sessionType} />
        )}
      {sharedHistoryMarkers.top.length > 0 && (
        <SharedHistoryMarkerLine
          markers={sharedHistoryMarkers.top}
          onManageSharing={handleManageSharing}
        />
      )}
      {/* Combine messages + streamingAnswer for display */}
      {(() => {
        const isStreamingAnswer = (index: number) =>
          Boolean(chatState.streamingAnswer) && index === answersForSpreadsheet.length - 1

        return answersForSpreadsheet.map((answer: [string, ChatAppResponse], index: number) => {
          // Skip invalid messages (missing response or invalid structure)
          if (
            !answer ||
            !Array.isArray(answer) ||
            answer.length < 2 ||
            !answer[1] ||
            typeof answer[1] !== "object"
          ) {
            return null
          }
          const answerKey = getMessageKey(answer[1], "answer", index)
          try {
            const shouldShowUserMessage =
              currentUserAccessMode !== "specific_message" && answer[0] && answer[0].trim()

            // For specific_message mode, ensure activities are filtered out and error is handled
            let filteredAnswer = answer[1]
            if (currentUserAccessMode === "specific_message" && filteredAnswer) {
              const needsFiltering =
                "activities" in filteredAnswer ||
                ("error" in filteredAnswer && !filteredAnswer.error) ||
                ("error_id" in filteredAnswer && !filteredAnswer.error_id)

              if (needsFiltering) {
                filteredAnswer = { ...filteredAnswer }
                delete filteredAnswer.activities
                // Remove error/error_id if they are null/undefined to prevent "Unknown error"
                if ("error" in filteredAnswer && !filteredAnswer.error) {
                  delete filteredAnswer.error
                }
                if ("error_id" in filteredAnswer && !filteredAnswer.error_id) {
                  delete filteredAnswer.error_id
                }
              }
            }

            const messageId =
              filteredAnswer.messageId || getResponseContext(filteredAnswer).message_id || undefined
            const beforeMarkers = messageId ? sharedHistoryMarkers.before[messageId] : undefined
            const afterMarkers = messageId ? sharedHistoryMarkers.after[messageId] : undefined

            return (
              <section key={answerKey} className="flex flex-col gap-6">
                {beforeMarkers?.length ? (
                  <SharedHistoryMarkerLine
                    markers={beforeMarkers}
                    onManageSharing={handleManageSharing}
                  />
                ) : null}
                {spreadSheetData.length > 0 && index === spreadSheetChatIndex && (
                  <SpreadsheetViewer data={spreadSheetData} />
                )}
                {shouldShowUserMessage &&
                  (() => {
                    const parsed = parseFollowupMessage(answer[0])
                    return (
                      <Chat.UserMessage
                        followup={parsed.isFollowup ? { question: parsed.question } : undefined}
                        message={answer[0]}
                      />
                    )
                  })()}
                <Chat.SystemMessage
                  showAnalysisPanelIcon
                  allowWebSearchAction={showWebSearchOption}
                  answer={filteredAnswer}
                  availableModels={availableGptModels}
                  citationSection={defaultSearchServiceId}
                  currentModelId={selectedReasoningModel?.id}
                  hideActivityHistory={useWorkspace}
                  index={index}
                  isLastMessage={index === answersForSpreadsheet.length - 1}
                  isReader={isReader}
                  isStreaming={isStreamingAnswer(index)}
                  messageId={filteredAnswer.messageId}
                  moduleName={moduleName}
                  noCitation={false}
                  sessionId={currentSessionId ?? undefined}
                  sessionType={sessionType}
                  userQuery={answer[0]}
                  activityLoadingPlaceholder={
                    isDeepAwaitingPlanApproval
                      ? t("deepExplorer.awaiting_plan_approval", {
                          defaultValue: "Awaiting plan approval",
                        })
                      : undefined
                  }
                  showFollowupQuestions={
                    suggestFollowupQuestions && answersForSpreadsheet.length - 1 === index
                  }
                  onBreakOutChat={handleBreakOutFromAIResponse}
                  onFollowupQuestionClicked={(q) => handleSendQuestion({ question: q })}
                  onMessageUpdate={handleMessageUpdate}
                  onSendFollowUp={handleSendFollowUp}
                  onShareMessage={handleShareMessage}
                  onSwitchModel={handleSwitchModelAction}
                  onTryAgain={handleTryAgainAction}
                />
                {/* Steer markers (issue #2468): purely informational — a
                    delivered note has no turn of its own, so it's rendered
                    as an extra bubble alongside (never replacing) the real
                    message pair at this index, showing which response
                    picked it up. */}
                {(chatState.messageSteerMarkers[index] ?? []).map((steerText, steerIndex) => (
                  <Chat.UserMessage
                    key={`${answerKey}-steer-${steerIndex}`}
                    message={steerText}
                    provenance={{ kind: "steered", text: steerText }}
                  />
                ))}
                {afterMarkers?.length ? (
                  <SharedHistoryMarkerLine
                    markers={afterMarkers}
                    onManageSharing={handleManageSharing}
                  />
                ) : null}
                {/* Inline Approve & Run row — shortcuts the user past
                  opening the side panel when a plan is awaiting
                  decision. Wrapped in SystemMessageLayout so it
                  inherits the same avatar offset + content frame as
                  the assistant answer above (left edge + width
                  match). */}
                {enableDeep &&
                  deepPlanProposed &&
                  !useWorkspace &&
                  index === answersForSpreadsheet.length - 1 && (
                    <SystemMessageLayout icon={<div aria-hidden="true" className="size-8" />}>
                      <SystemMessageLayout.Content>
                        <div
                          className="bg-muted/30 border-border/60 flex w-full items-center gap-3 rounded-md border px-3 py-2 text-sm"
                          role="group"
                          aria-label={t("deepExplorer.plan_ready_aria", {
                            defaultValue: "Plan ready for approval",
                          })}
                        >
                          <span className="flex-1 font-medium">
                            {t("deepExplorer.plan_ready", {
                              defaultValue: "Plan ready",
                            })}
                          </span>
                          <button
                            type="button"
                            className={cn(
                              buttonVariants({ size: "sm", variant: "default" }),
                              "h-7 px-3 text-xs"
                            )}
                            onClick={() => {
                              if (!currentSessionId) return
                              if (deepProposedTodos.length > 0) {
                                replaceDeepState(currentSessionId, {
                                  todos: deepProposedTodos,
                                })
                              }
                              setDeepPlanProposed(currentSessionId, false)
                              sendPlanDecision("approve")
                            }}
                          >
                            {t("deepExplorer.approve_and_run", {
                              defaultValue: "Approve & Run",
                            })}
                          </button>
                          <button
                            type="button"
                            className={cn(
                              buttonVariants({ size: "sm", variant: "ghost" }),
                              "h-7 px-3 text-xs"
                            )}
                            onClick={() => {
                              setDeepPanelOpen(true)
                              openDeepExplorerPanel()
                            }}
                          >
                            {t("deepExplorer.review_plan", {
                              defaultValue: "Review →",
                            })}
                          </button>
                        </div>
                      </SystemMessageLayout.Content>
                    </SystemMessageLayout>
                  )}
                {/* Smart follow-ups — show only on the last assistant
                  turn when Deep Explorer emitted suggestions. Hidden in
                  workspace mode where they render inside the workspace
                  center column (next to the deliverable they correspond
                  to) via WorkspaceLayout's `followupsSlot`. */}
                {enableDeep &&
                  !useWorkspace &&
                  index === answersForSpreadsheet.length - 1 &&
                  !isStreamingAnswer(index) &&
                  deepFollowups.length > 0 && (
                    <SmartFollowupCards
                      suggestions={deepFollowups as FollowupSuggestion[]}
                      onSelect={(s) => {
                        void handleSendQuestion({
                          question: composeFollowupQuestion(s),
                        })
                      }}
                    />
                  )}
              </section>
            )
          } catch {
            return null
          }
        })
      })()}
      {sharedHistoryMarkers.bottom.length > 0 && (
        <SharedHistoryMarkerLine
          markers={sharedHistoryMarkers.bottom}
          onManageSharing={handleManageSharing}
        />
      )}
      {chatState.isLoading && !chatState.streamingAnswer && (
        <ExplorerLoadingState
          currentUserAccessMode={currentUserAccessMode}
          lastQuestion={chatState.lastQuestion[0]}
          sessionId={currentSessionId ?? undefined}
        />
      )}
      {sessionErrorAlreadyInThread(chatState) ? (
        // The failure card is already on the last message (`SystemMessage`).
        // Do not draw a second `Chat.Error`. Ordinary Retry/Continue stay,
        // on the same resend path; fair-use and plan-upgrade refusals return
        // no actions from `threadFailureRecoveryActions`.
        <AgentFailureRecovery
          actions={threadFailureRecoveryActions(chatState)}
          onContinue={handleContinue}
          onRetry={() =>
            handleSendQuestion(
              getRetryRequestForLastQuestion(chatState.lastQuestion, chatState.lastRequestAgentId)
            )
          }
        />
      ) : null}
      {chatState.error && !sessionErrorAlreadyInThread(chatState) ? (
        <>
          {!chatState.canContinue &&
            currentUserAccessMode !== "specific_message" &&
            chatState.lastQuestion[0] &&
            (() => {
              const parsed = parseFollowupMessage(chatState.lastQuestion[0])
              return (
                <Chat.UserMessage
                  followup={parsed.isFollowup ? { question: parsed.question } : undefined}
                  message={chatState.lastQuestion[0]}
                />
              )
            })()}
          <Chat.Error
            canContinue={chatState.canContinue}
            error={(chatState.error as Error).toString()}
            errorCode={getSessionErrorCode(chatState)}
            onContinue={handleContinue}
            onRetry={
              chatState.lastQuestion[0]
                ? () =>
                    handleSendQuestion(
                      // Resend the *same* request — preserve the uploaded files
                      // (lastQuestion[1]) and agent id, not just the text, so a
                      // retry after a stall doesn't silently send a different
                      // request than the one that failed.
                      getRetryRequestForLastQuestion(
                        chatState.lastQuestion,
                        chatState.lastRequestAgentId
                      )
                    )
                : undefined
            }
          />
        </>
      ) : null}
      {aborted ? (
        <>
          {!chatState.canContinue &&
            currentUserAccessMode !== "specific_message" &&
            aborted[0] &&
            (() => {
              const parsed = parseFollowupMessage(aborted[0])
              return (
                <Chat.UserMessage
                  followup={parsed.isFollowup ? { question: parsed.question } : undefined}
                  message={aborted[0]}
                />
              )
            })()}
          <Chat.Error
            canContinue={chatState.canContinue}
            error={t("pages.common.request_aborted") + "\n\n" + (aborted[1] ? aborted[1] : "")}
            onContinue={handleContinue}
          />
        </>
      ) : null}
      <div ref={chatMessageStreamEnd} />
    </Chat.Content>
  )

  // Steer stage-1: while a steerable turn is running the composer sends
  // steering notes into it instead of starting a new turn. Returning false
  // keeps the draft in the composer when the note couldn't be sent.
  const handleComposerSend = (question: string) => {
    if (steerComposerActive) {
      // Steering notes are text-only — staged attachments would be silently
      // dropped by the steer frame, so block the send instead.
      if (files.length > 0) {
        toast.error(
          t("questioninput.steer_files_blocked", {
            defaultValue:
              "Attachments can't be sent with a steering note — remove them or wait for the response to finish.",
          })
        )
        return false
      }
      return sendSteerNote(question) ? undefined : false
    }
    return handleSendQuestion({ question })
  }

  const effectiveComposerPlaceholder = steerComposerActive
    ? t("questioninput.steer_placeholder", {
        defaultValue: "Add guidance for the current response…",
      })
    : composerPlaceholder
  const steerSendLabel = steerComposerActive
    ? t("questioninput.send_steer_note", { defaultValue: "Send steering note" })
    : undefined

  const renderComposer = () => (
    <Chat.Footer
      clearOnSend
      focusOnMount
      askAboutText={askAboutText ?? undefined}
      availableFiles={files}
      availableReasoningModels={selectableModels}
      cancelAvailable={!isCurrentSessionUncancellable}
      changeTokenCounts={changeTokenCounts}
      compactFileList={!useWorkspace}
      customSendQuestionLabel={steerSendLabel}
      defaultFilePickerSource="local"
      dropzoneOptions={dropzoneOptions}
      effortOverride={effortOverride}
      filesInIndex={filesInIndex}
      hideVoiceInputButton={true}
      hideVoiceMode={!isVoiceModeEnabled}
      isReader={isReader}
      maxFiles={MAX_FILES_UPLOAD}
      maxTokens={translator?.enabled ? Number.POSITIVE_INFINITY : maxTokens}
      modelOverride={modelOverride}
      placeholder={effectiveComposerPlaceholder}
      projectId={projectId}
      reasoningMode={reasoningMode}
      removingFileName={removingFileName}
      resetFiles={handleResetFiles}
      responseTiming={responseTiming}
      selectedDocuments={selectedDocuments}
      selectedReasoningModel={selectedReasoningModel}
      sessionFileRefs={sessionFileRefs}
      sessionType={sessionType}
      showChatOptions={true}
      showHighReasoning={false}
      showReasoningMode={showReasoningMode}
      totalTokenCount={totalTokenCount}
      voiceSessionRoutePrefix={voiceSessionRoutePrefix}
      actionButtons={
        showStartResearchButton ? (
          <button
            className={cn(
              buttonVariants({
                size: "md",
                variant: "default",
              }),
              "gap-2"
            )}
            onMouseDown={(e) => {
              e.preventDefault()
              void handleStartResearch()
            }}
          >
            <Play className="size-4" />
            {t("questioninput.start_research")}
          </button>
        ) : null
      }
      composerBar={
        <ComposerQueueBar
          // Visibility is owned by the bar itself (queued / pending steers /
          // pendingRedirect). Stop lives in the composer send slot
          // (showCancelOption) — no Generating row / onStop here.
          pendingRedirectText={chatState.pendingRedirect?.text}
          queuedMessages={chatState.queuedMessages}
          pendingSteerNotes={chatState.steerNotes
            .filter((note) => note.status === "pending")
            .map((note) => ({ id: note.steer_id, text: note.content }))}
          onRemove={handleRemoveQueuedMessage}
          onForce={
            steerStopAvailableForRunningTurn
              ? (id, text) => {
                  if (requestForceRedirect(text)) {
                    handleRemoveQueuedMessage(id)
                  }
                }
              : undefined
          }
          onForcePendingSteer={
            steerStopAvailableForRunningTurn
              ? (id, text) => {
                  if (!requestForceRedirect(text)) return
                  const sid = explorerStore.getState().currentSessionId
                  if (!sid) return
                  const notes = explorerStore.getState().sessionStates[sid]?.steerNotes ?? []
                  explorerStore.getState().updateSession(sid, {
                    steerNotes: notes.filter((note) => note.steer_id !== id),
                  })
                }
              : undefined
          }
          onRemovePendingSteer={(id) => {
            const sid = explorerStore.getState().currentSessionId
            if (!sid) return
            const notes = explorerStore.getState().sessionStates[sid]?.steerNotes ?? []
            explorerStore.getState().updateSession(sid, {
              steerNotes: notes.filter((note) => note.steer_id !== id),
            })
          }}
          onSteer={
            steerAvailableForRunningTurn
              ? (id, text) => {
                  if (sendSteerNote(text)) {
                    handleRemoveQueuedMessage(id)
                  }
                }
              : undefined
          }
        />
      }
      disableSend={
        isReader ||
        ((chatState.isLoading || chatState.isStreaming || chatState.isAwaitingSave) &&
          // Steer stage-1: keep the composer enabled while the running turn
          // accepts steering notes (ChatFooter renders Send alongside Stop
          // exactly when showCancelOption && !disableSend).
          !steerComposerActive)
      }
      showCancelOption={
        // `isReader` mirrors the backend's write-access gate for cancel
        // (`can_send_message` — see `chat_turns.py`'s `cancel_turn`, the same
        // check `disableSend` above already uses for Send). A viewer without
        // write access can never make a cancel succeed, so Stop must not be
        // rendered as if it could (issue #2513 finding 2).
        !isReader && (chatState.isLoading || chatState.isStreaming) && !isDeepAwaitingPlanApproval
      }
      onBeforeVoiceModeStart={handleBeforeVoiceModeStart}
      onCancel={handleCancelRequest}
      onClearAskAboutText={handleClearAskAboutText}
      onDocumentsSelected={handleDocumentsSelected}
      onEffortOverrideChange={setEffortOverride}
      onFilesAdded={handleAddFiles}
      onGetSendSessionContext={handleGetSendSessionContext}
      onGetToggleVoiceMode={handleGetToggleVoiceMode}
      onGraphFileSelected={handleGraphFileSelected}
      onHighReasoningChange={handleHighReasoningChange}
      onModelOverrideChange={setModelOverride}
      onOpenSelectedFilesContext={!useWorkspace ? openContextPanel : undefined}
      onQueue={canQueueMessages ? handleEnqueueMessage : undefined}
      onReasoningModeChange={handleReasoningModeChange}
      onRemoveDocument={handleRemoveDocument}
      onRemoveFile={handleRemoveContextFile}
      onSend={handleComposerSend}
      onSetQuestion={handleSetQuestion}
      onStreamingComplete={handleStreamingComplete}
      onStreamingVoiceMessage={handleStreamingVoiceMessage}
      onVoiceMessage={handleVoiceMessage}
      onFirstUserInput={() => {
        // The session is created on send (handleSendQuestion), not here: a
        // keystroke is not intent to chat, and creating on the first key left
        // an empty "New chat" row and a rewritten URL behind every abandoned
        // draft (#2963).
        // Freeze the rotating typewriter placeholder once the user starts typing.
        setHasTypedThisSession(true)
      }}
      onSlashCommandDetected={(command) => {
        // Mode commands (/deep, /plan) flip Deep mode on so the send
        // routes through the Deep Explorer graph and opens the agent panel.
        if (command === "deep" || command === "plan") {
          handleDeepModeChange(true)
          return
        }
        // Reasoning commands apply one-shot to the session. `bigbrain` is
        // spelled `big_brain` in the store.
        if (command === "auto" || command === "fast" || command === "thinking") {
          handleReasoningModeChange(command)
          return
        }
        if (command === "bigbrain") {
          handleReasoningModeChange("big_brain")
          return
        }
      }}
    >
      <ExplorerFooterToolbar
        activeTodoCount={activeTodoCount}
        disabledPluginIds={disabledPluginIds}
        enableAgent={enableAgent}
        enableDeep={enableDeep}
        enableM365={enableM365}
        enableNetbox={enableNetbox}
        enablePlugins={enablePlugins}
        enableServiceDesk={enableServiceDesk}
        enableTreeRetrieval={enableTreeRetrieval}
        enableWebSearch={enableWebSearch}
        enableXOne={enableXOne}
        hasAgentModeFeature={hasAgentModeFeature}
        hideVoiceMode={!isVoiceModeEnabled}
        isPromptsFeatureEnabled={isPromptsFeatureEnabled ?? false}
        isStreaming={chatState.isStreaming}
        personalSearch={personalSearch}
        sessionType={sessionType}
        showM365Option={showM365Option}
        showNetboxOption={showNetboxOption}
        showPluginsOption={showPluginsOption}
        showServiceDeskOption={showServiceDeskOption}
        showWebSearchOption={showWebSearchOption}
        showXOneOption={showXOneOption}
        translator={translator}
        onAgentChange={handleAgentChange}
        onDeepChange={handleDeepModeChange}
        onInstalledPluginsChange={handleInstalledPluginsChange}
        onM365Change={handleM365Change}
        onManagePlugins={pluginsPageAvailable ? handleManagePlugins : undefined}
        onNetboxChange={handleNetboxChange}
        onPluginDisabledChange={handlePluginDisabledChange}
        onPluginsChange={handlePluginsChange}
        onServiceDeskChange={handleServiceDeskChange}
        onStartVoiceMode={handleStartVoiceModeFromTools}
        onTreeRetrievalChange={handleTreeRetrievalChange}
        onWebSearchChange={handleWebSearchChange}
        onXOneChange={handleXOneChange}
        onPromptClicked={(prompt: PromptLibraryItem) => {
          // Set the prompt content in the question input
          if (setQuestionFunction) {
            setQuestionFunction(prompt.content)
          }
          markNextMessageAsPromptTemplate()
          setSelectedPrompt(prompt.id, prompt.title)
          applyPromptSettings(prompt)
        }}
      />
    </Chat.Footer>
  )

  return (
    <Chat>
      <div className="flex h-full min-h-0 flex-1 overflow-hidden">
        <SidebarInset className="module-enter relative h-full min-h-0 min-w-0 flex-1 overflow-hidden md:peer-data-[variant=inset]:my-0 md:peer-data-[variant=inset]:mr-0">
          <Chat.Header className="absolute top-0 right-0 left-0 z-50 justify-between lg:justify-end">
            <div className="mr-auto flex items-center gap-2">
              <div className="lg:hidden">
                <StartNewChat onStartNewChatClick={handleStartNewChat} />
              </div>
              {isReader && (
                <Badge
                  className="border-foreground bg-accent text-accent-foreground h-7 border px-3"
                  variant="outline"
                >
                  <Eye className="mr-1.5 size-3" />
                  {t("chatsession.reader_badge", { defaultValue: "READER" })}
                </Badge>
              )}
              {currentSessionId && (
                <Button
                  className="bg-background/85 text-foreground hover:bg-background h-7 gap-1.5 px-3"
                  disabled={personalSearch.tainted}
                  size="sm"
                  variant="outline"
                  title={
                    personalSearch.tainted
                      ? t("personalSources.search.shareBlocked", {
                          defaultValue: "A chat with your personal content cannot be shared.",
                        })
                      : undefined
                  }
                  onClick={() => setShowShareDialog(true)}
                >
                  <Share2 className="size-3" />
                  {t("sharing.share", { defaultValue: "Share" })}
                </Button>
              )}
            </div>
          </Chat.Header>
          <HeaderRightSlot>
            {/* UI-7: research-runs panel opener — only renders when the
                user actually has runs to look at, so the toolbar stays
                uncluttered for everyone else. Hidden on small screens to
                stop the chrome row from crowding the workspace mobile
                tabs; users on mobile see live activity for the current
                run via the Activity tab itself. */}
            <div className="hidden sm:contents">
              <ResearchRunsHeaderButton onOpen={openResearchRunsPanel} />
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={sidebarTriggerLabel}
                  aria-pressed={isContentSidebarOpen}
                  data-testid="ai-explorer-sidebar-trigger"
                  size="icon"
                  type="button"
                  variant="ghost"
                  className={cn(
                    buttonVariants({
                      size: "icon",
                      variant: "icon",
                      className: "bg-background",
                    }),
                    "relative size-9 rounded-full",
                    // Use a solid filled background for the "panel open"
                    // indicator. A translucent ``bg-primary/10`` would let
                    // long chat bubbles bleed through the icon, and an
                    // orange ring fights with the design system rule that
                    // primary orange is reserved for CTAs.
                    isContentSidebarOpen &&
                      "border-foreground bg-accent text-accent-foreground border"
                  )}
                  onClick={toggleSharedSidebarPanel}
                >
                  {isContentSidebarOpen ? (
                    <PanelRightClose className="size-5" />
                  ) : (
                    <PanelRightOpen className="size-5" />
                  )}
                  {enableDeep && canUseLegacyDeepPanel && activeTodoCount > 0 && (
                    <span className="border-foreground bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 min-w-4 rounded-full border px-1 text-[10px] leading-4 tabular-nums">
                      {activeTodoCount}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{sidebarTriggerLabel}</TooltipContent>
            </Tooltip>
          </HeaderRightSlot>
          {(() => {
            // Phase 4.3b: in Deep mode (with the kill-switch on) the
            // conversation + composer + activity peek render inside the new
            // 3-column WorkspaceLayout instead of the legacy single-column
            // chat. Plain Explorer (`enableDeep === false`) stays on the
            // legacy path verbatim — no behaviour change for that branch.
            // `useWorkspace` is hoisted to the top of the render path; the
            // legacy-panel effects use `hasNewDeepChrome` to also cover the
            // pre-session armed state.
            const conversation = (
              <TextSelectionProvider
                currentSessionId={currentSessionId}
                projectId={projectId}
                sessionType={sessionType}
                onSetAskAboutText={handleSetAskAboutText}
                onSetQuestion={setQuestionFunction ?? undefined}
                onSendQuestion={async ({ question, startResearch, forceWebSearch }) =>
                  handleSendQuestion({ question, startResearch, forceWebSearch })
                }
              >
                {renderConversationContent()}
                <ToolApprovalOverlay
                  activeSessionId={currentSessionId ?? null}
                  approvals={chatState.toolApprovals ?? []}
                  durableTurn={chatState.durableTurn ?? null}
                  sidebarExpanded={isContentSidebarOpen}
                  toolApprovalSessionId={chatState.toolApprovalSessionId ?? null}
                />
              </TextSelectionProvider>
            )
            if (useWorkspace) {
              return (
                <WorkspaceLayout
                  agentRunActive={isCurrentSessionAgentRunActive}
                  citationSection={defaultSearchServiceId}
                  composerSlot={renderComposer()}
                  conversationSlot={conversation}
                  sessionId={currentSessionId}
                  sessionType={sessionType}
                  traceFocusedMissionId={traceFocusMission}
                  activitySlot={
                    <DeepExplorerActivityPeek
                      citationSection={defaultSearchServiceId}
                      missionFilter={traceFocusMission}
                      sessionTitle={deepSessionTitle}
                      sessionType={sessionType}
                      onApproveInBackground={handleApproveInBackground}
                      onAskUserResponse={sendAskUserResponse}
                      onClearMissionFilter={handleClearTraceFocus}
                      onPlanDecision={sendPlanDecision}
                      onRewindToPlan={rewindToPlan}
                      onSendUserNote={sendUserNote}
                    />
                  }
                  followupsSlot={
                    deepFollowups.length > 0 && !chatState.isLoading ? (
                      <SmartFollowupCards
                        suggestions={deepFollowups as FollowupSuggestion[]}
                        onSelect={(s) => {
                          void handleSendQuestion({
                            question: composeFollowupQuestion(s),
                          })
                        }}
                      />
                    ) : null
                  }
                  onViewTrace={handleViewTrace}
                  onAction={(actionId, artifact, extra) => {
                    const artifactSessionId = artifact.conversation_id
                    if (
                      !artifactSessionId &&
                      (artifact.type === "pending_action" ||
                        artifact.type === "plan" ||
                        artifact.type === "ask_user")
                    ) {
                      logger.warn(
                        "[workspace] actionable artifact has no conversation_id — refresh required"
                      )
                      return
                    }
                    // Phase 9.2 — HITL approval gate. PendingActionRenderer
                    // dispatches "approve"/"reject" for pending_action
                    // artifacts; we forward those over the deep WS so the
                    // backend's `request_action_approval` helper unblocks
                    // and either runs the side effect (approve) or rolls
                    // it back (reject).
                    if (
                      artifact.type === "pending_action" &&
                      (actionId === "approve" || actionId === "reject")
                    ) {
                      sendArtifactDecision(actionId, artifact.id, artifactSessionId)
                      return
                    }
                    // Phase 1 (workspace-focus): plan-artifact approval.
                    // PlanRenderer's inline controls dispatch `plan.*`
                    // action ids; we route them to the existing plan-decision
                    // WS contract. Reject maps to the backend's "revise"
                    // decision because that's the existing
                    // reject-with-feedback variant in `sendPlanDecision`'s
                    // union — see use-ai-explorer-chat.ts.
                    if (artifact.type === "plan") {
                      if (actionId === "plan.approve") {
                        if (artifactSessionId) {
                          const artifactProposedTodos = getArtifactSessionProposedTodos(
                            explorerStore.getState(),
                            artifactSessionId
                          )
                          if (artifactProposedTodos.length > 0) {
                            replaceDeepState(artifactSessionId, { todos: artifactProposedTodos })
                          }
                          setDeepPlanProposed(artifactSessionId, false)
                        }
                        sendPlanDecision("approve", { targetSessionId: artifactSessionId })
                        return
                      }
                      if (actionId === "plan.approve_background") {
                        if (artifactSessionId) {
                          const artifactProposedTodos = getArtifactSessionProposedTodos(
                            explorerStore.getState(),
                            artifactSessionId
                          )
                          if (artifactProposedTodos.length > 0) {
                            replaceDeepState(artifactSessionId, { todos: artifactProposedTodos })
                          }
                          setDeepPlanProposed(artifactSessionId, false)
                        }
                        handleApproveInBackground({ targetSessionId: artifactSessionId })
                        return
                      }
                      if (actionId === "plan.reject") {
                        const feedback = extra?.feedback?.trim() ?? ""
                        if (!feedback) {
                          // Defensive: PlanRenderer's submit button is disabled
                          // when feedback is empty, but if a future caller
                          // dispatches without feedback, drop on the floor
                          // rather than send an empty revise frame.
                          logger.warn("[workspace] plan.reject without feedback — ignored")
                          return
                        }
                        if (artifactSessionId) {
                          setDeepPlanProposed(artifactSessionId, false)
                        }
                        sendPlanDecision("revise", { feedback, targetSessionId: artifactSessionId })
                        return
                      }
                      if (actionId === "plan.cancel") {
                        dispatchPlanCancel({
                          sessionId: artifactSessionId,
                          sendPlanDecision,
                          setDeepPlanProposed,
                        })
                        return
                      }
                    }
                    // Phase 2 (workspace-focus): ask-user as artifact.
                    // AskUserRenderer dispatches `ask_user.respond` (with
                    // answers) and `ask_user.skip`. Both flow through the
                    // existing `sendAskUserResponse` callback (skip = empty
                    // answers, matching how AskUserModal's onSkip treats it
                    // in legacy mode).
                    if (artifact.type === "ask_user") {
                      if (actionId === "ask_user.respond" || actionId === "ask_user.skip") {
                        const artifactAskId = (
                          artifact.payload as {
                            ask_id?: string | null
                          }
                        ).ask_id
                        const pendingAsk = getDeepStateForSession(
                          explorerStore.getState(),
                          artifactSessionId
                        )
                        const pendingAskId = pendingAsk.askUserPayload?.ask_id
                        const isActiveAsk = isActiveAskUserPrompt({
                          askUserProposed: pendingAsk.askUserProposed,
                          pendingAskId,
                          artifactAskId,
                        })
                        if (!isActiveAsk) {
                          logger.warn(
                            `[workspace] ${actionId} for inactive ask_user artifact ${artifact.id} — ignored`
                          )
                          return
                        }
                        const answers =
                          actionId === "ask_user.respond" ? (extra?.answers ?? {}) : {}
                        sendAskUserResponse(answers, artifactSessionId, artifactAskId)
                        return
                      }
                    }
                    // edit_in_canvas — reuse the existing canvas surface from
                    // normal Explorer (system-message.tsx's "Create Document"
                    // path). Persist the artifact's body as a real document,
                    // then open it in the canvas sidebar with editing enabled
                    // and full version history. Any document-shaped artifact
                    // can dispatch this; we just need a body string.
                    if (actionId === "edit_in_canvas") {
                      // Guard against double-clicks: createDocumentApi mutates
                      // server state, so a second click before the first
                      // resolves would silently create a duplicate document.
                      if (editInCanvasInFlightRef.current.has(artifact.id)) {
                        return
                      }
                      const body =
                        typeof (artifact.payload as { body?: unknown })?.body === "string"
                          ? (artifact.payload as { body: string }).body
                          : ""
                      if (!body.trim()) {
                        logger.warn("[workspace] edit_in_canvas: empty body — ignored")
                        return
                      }
                      const firstNonEmpty = body
                        .split("\n")
                        .map((line) => line.trim())
                        .find((line) => line.length > 0)
                      const derivedTitle = (firstNonEmpty ?? "")
                        .replace(/^#+\s*/, "")
                        .slice(0, 80)
                        .trim()
                      const fallbackTitle = artifact.title?.trim() || "Document from response"
                      editInCanvasInFlightRef.current.add(artifact.id)
                      void (async () => {
                        try {
                          const settingsService = SettingsService.getInstance()
                          const created = await createDocumentApi(
                            {
                              title: derivedTitle || fallbackTitle,
                              content: body,
                              contentFormat: "markdown",
                              sourceSessionId: currentSessionId ?? undefined,
                            },
                            settingsService.token
                          )
                          void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
                          openCanvasInSidebar(
                            {
                              documentId: created.id,
                              activeDocumentCanEdit: true,
                              sessionId: currentSessionId ?? undefined,
                              sessionFilenames: [],
                              title: created.title,
                              content: created.content || body,
                              versions: (created.versions ?? []).map((v) => ({
                                version: v.version,
                                content: v.content,
                                timestamp: v.timestamp,
                                is_ai_edit: v.edit_source === "ai",
                                ai_edit_preview: v.edit_preview,
                              })),
                              selectedModelId: selectedReasoningModel?.id ?? "",
                              lastSavedAt: created.updated ?? null,
                            },
                            { title: created.title }
                          )
                          toast.success(
                            t("documents.created_from_response", {
                              defaultValue: "Document created from response",
                            })
                          )
                        } catch (err) {
                          logger.warn(`[workspace] edit_in_canvas failed: ${String(err)}`)
                          toast.error(
                            t("documents.create_failed", {
                              defaultValue: "Couldn't open the document in canvas",
                            })
                          )
                        } finally {
                          editInCanvasInFlightRef.current.delete(artifact.id)
                        }
                      })()
                      return
                    }
                    // Other action types (copy/download/etc.) are already
                    // intercepted inside their respective renderers; log
                    // anything that reaches here so the dispatch path stays
                    // observable in dev.
                    logger.info("[workspace] action", { actionId, artifactId: artifact.id })
                  }}
                />
              )
            }
            return (
              <>
                {conversation}
                {renderComposer()}
              </>
            )
          })()}
        </SidebarInset>
      </div>

      {projectId && (
        <ProjectsDialog
          open={showProjectsPanel}
          sessionType={sessionType}
          onOpenChange={setShowProjectsPanel}
        />
      )}

      <AgentModeInfoModal open={showAgentModeInfo} onClose={() => setShowAgentModeInfo(false)} />
      <M365InfoModal
        open={showM365Info}
        onApprove={handleM365Approve}
        onDecline={handleM365DialogDecline}
        onDismiss={handleM365DialogDismiss}
      />
      <VoiceModeInfoModal
        engine={voiceEngine}
        open={showVoiceModeInfo}
        onAccept={handleVoiceModeAccept}
        onClose={() => setShowVoiceModeInfo(false)}
      />
      <ImageReasoningWarningModal
        open={showImageReasoningWarning}
        onCancel={handleImageReasoningWarningCancel}
        onClose={handleImageReasoningWarningClose}
      />
      <WorkflowInputDialog
        open={!!workflowToRun}
        workflow={workflowToRun}
        onOpenChange={(open) => !open && setWorkflowToRun(null)}
        onRun={handleWorkflowRun}
      />

      {currentSessionId && (
        <ChatShareDialog
          chatStore={explorerStore}
          currentMessageId={shareMessageId ?? currentMessageId}
          open={showShareDialog}
          session={currentSession}
          sessionId={currentSessionId}
          onLeave={handleSharedLeaveRedirect}
          onOpenChange={(open) => {
            setShowShareDialog(open)
            // Reset shareMessageId when dialog closes
            if (!open) {
              setShareMessageId(undefined)
            }
          }}
        />
      )}
    </Chat>
  )
}
